import { Fragment, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
  adminApi,
  AdminOverview,
  AdminFeatures,
  AdminSplitter,
  AdminUserRow,
  AdminUsersResp,
  AdminUserDetail,
  AdminUninstallsResp,
  AdminReviewsResp,
  AdminPlanTarget,
  AdminSettings,
  CheckoutMode,
  PriceSet,
  PlanLabel,
} from '../../lib/adminApi';
import { ApiError } from '../../lib/api';
import { STEM_COLOR } from '../../components/studioIcons';
import { fmtRelativeDate, stemLabel } from '../../lib/format';
import { Logo } from '../../components/Logo';
import { SeoModule } from './seo/SeoModule';
import { SEO_TABS, SeoTab, adminUrlGet, adminUrlSet } from './seo/seoShared';
import './admin.css';

// ── helpers ────────────────────────────────────────────────────────────────
function initials(name: string | null | undefined, email: string): string {
  const base = (name && name.trim()) || email || '?';
  const parts = base.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.trim().slice(0, 2).toUpperCase();
}
function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function rel(iso: string | null | undefined): string {
  return iso ? fmtRelativeDate(iso) : '—';
}
// Only turn a stored source URL into a clickable link if it's an https YouTube
// URL — blocks javascript:/data: links a user could plant to attack the admin
// session. React escapes the text content; this guards the href specifically.
function safeYouTubeUrl(u: string | null | undefined): string | null {
  if (!u) return null;
  try {
    const p = new URL(u);
    if (p.protocol !== 'https:') return null;
    if (!/(^|\.)youtube\.com$|(^|\.)youtu\.be$/i.test(p.hostname)) return null;
    return p.href;
  } catch {
    return null;
  }
}
// Compact label for a separation event in the activity feed.
function surfaceLabel(s: string | null | undefined): string {
  if (s === 'extension') return 'Extension';
  if (s === 'studio') return 'Studio';
  if (s === 'android') return 'Android';
  return s || 'unknown';
}
// One user's trail, read back. The names are shared across the extension, the Studio
// and the app (the `surface` column says which), so this map is the whole vocabulary
// in one place. Anything not listed falls back to a tidied-up version of the raw name,
// which is what makes adding an event on a client a one-side change.
const EVENT_LABEL: Record<string, string> = {
  rl_app_open: 'Opened the app',
  rl_app_signin: 'Signed in',
  rl_signout: 'Signed out',
  rl_app_song_add: 'Added a song',
  rl_demo_open: 'Opened the demo',
  rl_separate: 'Started a separation',
  rl_app_separate_done: 'Separation finished',
  rl_app_separate_failed: 'Separation failed',
  rl_separate_cancel: 'Stopped a separation',
  rl_app_pack: 'Downloaded an engine',
  rl_wall_hit: 'Hit a free limit',
  rl_upgrade_modal_open: 'Saw the Pro offer',
  rl_plan_pick: 'Picked a plan',
  rl_upgrade_click: 'Tapped Continue',
  rl_upgrade_modal_close: 'Closed the offer',
  rl_purchase: 'Upgraded',
  // ⚠️ Never a success — a grant is rl_purchase. The auto-label would read "Purchase end", which
  // sounds like a completed sale and means the opposite. The meta line says how it failed.
  rl_app_purchase_end: 'Purchase did not go through',
  rl_app_demo_step: 'Walkthrough step',
  rl_app_play: 'Played a song',
  rl_app_chords_open: 'Opened chords',
  rl_metronome: 'Used the metronome',
  rl_key_change: 'Changed the key',
  rl_speed_change: 'Changed the speed',
  rl_app_loop_save: 'Saved a loop',
  rl_mixer_mute: 'Muted a part',
  rl_export_done: 'Exported',
};
function eventLabel(e: string): string {
  if (EVENT_LABEL[e]) return EVENT_LABEL[e];
  const t = e.replace(/^rl_(app_)?/, '').replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}
// The four that explain an upgrade get the accent; a failure gets the warning colour.
// Everything else stays quiet, so a long trail still reads at a glance.
function eventTone(e: string): string | undefined {
  if (e === 'rl_purchase') return 'var(--ok, #35c77a)';
  if (e === 'rl_wall_hit' || e === 'rl_upgrade_modal_open' || e === 'rl_upgrade_click') return 'var(--accent)';
  // A lost sale reads in the same colour as a failed run: both are things that went wrong for them.
  if (e === 'rl_app_separate_failed' || e === 'rl_app_purchase_end') return 'var(--warn, #e0664b)';
  return undefined;
}
/**
 * Meta as one quiet line. Drops `ms` — that is the queue delay, spent above on the
 * timestamp rather than shown as a number nobody reads.
 */
function metaLine(meta: string | null): string {
  if (!meta) return '';
  try {
    const o = JSON.parse(meta) as Record<string, unknown>;
    return Object.entries(o)
      .filter(([k]) => k !== 'ms')
      .map(([k, v]) => `${k} ${v}`)
      .join(' · ');
  } catch {
    return '';
  }
}
/**
 * When it actually happened.
 *
 * The row is stamped by the server as it lands, and the phone holds a burst of taps
 * for a few seconds before sending them — so a whole burst would otherwise share one
 * timestamp and lose its order. Every event carries how old it was when it was sent
 * (`ms`), which puts it back where it belongs, to the second.
 */
function eventAt(e: { meta: string | null; created_at: string }): Date {
  const t = Date.parse(e.created_at);
  if (Number.isNaN(t)) return new Date(0);
  let age = 0;
  try {
    const o = JSON.parse(e.meta || '{}') as { ms?: unknown };
    if (typeof o.ms === 'number' && o.ms > 0 && o.ms < 7 * 86400_000) age = o.ms;
  } catch { /* meta is free-form; a bad one just means no correction */ }
  return new Date(t - age);
}
function clockOf(d: Date): string {
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}
function dayOf(d: Date): string {
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
}

function isExpired(e: unknown): boolean {
  return !!e && typeof e === 'object' && (e as ApiError).status === 403;
}
// How a user signs in, shown next to their address in the users list.
// Deliberately quiet — reference information, not a status to act on. The one
// loud case is an email+password account that never followed its activation
// link, since that is the state most likely to explain "I signed up but
// nothing works". Derived server-side; the password hash never reaches the UI.
const AUTH_METHOD_LABEL: Record<string, string> = {
  google: 'Google', password: 'Email', both: 'Google + Email',
  apple: 'Apple', 'google+apple': 'Google + Apple', 'apple+password': 'Apple + Email',
  all: 'Google + Apple + Email',
};
// Fall back to 'Google' only for a value this build has never heard of — the
// same thing the previous, un-mapped version did for every unknown.
function authMethodLabel(m?: string): string {
  return (m && AUTH_METHOD_LABEL[m]) || 'Google';
}

function AuthMethodTag({ method, activated }: { method?: string; activated?: boolean }) {
  if (!method) return null; // older API response — show nothing rather than guess
  const base: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.3,
    padding: '1px 6px',
    borderRadius: 5,
    flexShrink: 0,
    textTransform: 'uppercase',
    border: '1px solid transparent',
  };
  return (
    <>
      <span
        style={{
          ...base,
          color: method === 'google' ? 'var(--dim)' : 'var(--accent)',
          borderColor: 'var(--surface-3)',
          background: 'var(--surface-3)',
        }}
        title={`Signs in with ${authMethodLabel(method)}`}
      >
        {method === 'both' ? 'G+Email' : method === 'google+apple' ? 'G+Apple'
          : method === 'apple+password' ? 'Apple+Email' : method === 'all' ? 'G+A+Email'
          : authMethodLabel(method)}
      </span>
      {activated === false && (
        <span
          style={{ ...base, color: '#f59e0b', borderColor: 'currentColor', background: 'transparent' }}
          title="Signed up but never followed the activation link — this account cannot sign in yet"
        >
          Not activated
        </span>
      )}
    </>
  );
}

function planText(p: PlanLabel): string {
  return p === 'lifetime' ? 'Lifetime' : p === 'pro' ? 'Pro' : 'Free';
}
// Presence inferred from last login (we don't track suspend/idle server-side).
function presence(lastLogin: string | null | undefined): { label: string; color: string } {
  if (!lastLogin) return { label: 'New', color: 'var(--lifetime)' };
  const days = (Date.now() - new Date(lastLogin).getTime()) / 86400000;
  if (days <= 7) return { label: 'Active', color: 'var(--green)' };
  if (days <= 30) return { label: 'Recent', color: 'var(--amber)' };
  return { label: 'Idle', color: 'var(--faint)' };
}
function fmtMoney(n: number): string {
  const v = Math.round(n * 100) / 100; // 2-decimal, never whole-dollar rounding
  return '$' + (Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
}
// Sparkline path builder (ported from the design handoff).
function spark(data: number[], w: number, h: number, pad: number): { line: string; area: string } {
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const span = max - min || 1;
  const n = data.length;
  const xs = (i: number) => (n <= 1 ? w / 2 : (i / (n - 1)) * w);
  const ys = (v: number) => pad + (1 - (v - min) / span) * (h - 2 * pad);
  const pts = data.map((v, i) => [xs(i), ys(v)] as [number, number]);
  const line = 'M' + pts.map((p) => p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' L');
  const area = `${line} L${w},${h} L0,${h} Z`;
  return { line, area };
}
// Excel/Sheets evaluate a cell starting with = + - @ (or tab/CR) as a FORMULA, and
// wrapping it in quotes does not stop that — the quotes are stripped on import. The
// uninstall note is free text from an unauthenticated endpoint, so a stranger could
// otherwise land a formula in the owner's spreadsheet. Prefix a single quote to
// force it to text. Real numbers (including negatives) are left alone so numeric
// columns still import as numbers.
function csvCell(x: unknown): string {
  let s = String(x ?? '');
  if (/^[=+\-@\t\r]/.test(s) && !/^-?\d+(\.\d+)?$/.test(s)) s = "'" + s;
  return '"' + s.replace(/"/g, '""') + '"';
}
function downloadCsv(name: string, rows: (string | number)[][]) {
  const csv = rows.map((r) => r.map(csvCell).join(',')).join('\n');
  try {
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  } catch { /* ignore */ }
}

// Gravatar (the public, email-based standard) for users with no Google avatar.
// d=blank ⇒ transparent when there's no Gravatar, so the gradient initials show.
function useGravatars(emails: string[]): Record<string, string> {
  const [map, setMap] = useState<Record<string, string>>({});
  const key = emails.filter(Boolean).join(',');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!window.crypto?.subtle) return;
        const enc = new TextEncoder();
        const out: Record<string, string> = {};
        for (const e of key.split(',')) {
          if (!e) continue;
          const buf = await crypto.subtle.digest('SHA-256', enc.encode(e.trim().toLowerCase()));
          out[e] = Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
        }
        if (!cancelled) setMap(out);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [key]);
  return map;
}

function Avatar({ name, email, picture, gravatar, size }: { name: string | null; email: string; picture?: string | null; gravatar?: string; size: number }) {
  const src = picture || (gravatar ? `https://www.gravatar.com/avatar/${gravatar}?s=160&d=blank` : null);
  return (
    <span className="adm-avatar" style={{ width: size, height: size, fontSize: Math.round(size * 0.38), borderRadius: size >= 48 ? 16 : size >= 40 ? 11 : 10 }}>
      {initials(name, email)}
      {src && <img src={src} alt="" referrerPolicy="no-referrer" />}
    </span>
  );
}

function ThemeToggle({ theme, onToggle, big }: { theme: 'dark' | 'light'; onToggle: () => void; big?: boolean }) {
  const s = big ? 18 : 17;
  return (
    <button className="adm-iconbtn" onClick={onToggle} aria-label="Toggle theme"
      style={big ? { position: 'absolute', top: 20, right: 20, width: 42, height: 42, borderRadius: 12 } : undefined}>
      {theme === 'dark' ? (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="4.2" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>
      ) : (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" /></svg>
      )}
    </button>
  );
}

function useAdminTheme(): ['dark' | 'light', () => void] {
  // Remember the last-used theme: read the saved choice (rl-theme) directly so
  // it's restored even if the page's pre-paint script didn't run, falling back to
  // the current data-theme, then dark.
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    try {
      const saved = localStorage.getItem('rl-theme');
      if (saved === 'light' || saved === 'dark') return saved;
    } catch { /* ignore */ }
    return typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
  });
  // Keep the document in sync with the restored/selected theme.
  useEffect(() => { document.documentElement.setAttribute('data-theme', theme); }, [theme]);
  const toggle = useCallback(() => {
    setTheme((t) => {
      const next = t === 'dark' ? 'light' : 'dark';
      try { localStorage.setItem('rl-theme', next); } catch { /* ignore */ }
      return next;
    });
  }, []);
  return [theme, toggle];
}

// ════════════════════════════════════════════════════════════════════════════
export default function AdminApp() {
  const [phase, setPhase] = useState<'loading' | 'login' | 'app'>('loading');
  const [username, setUsername] = useState('');

  const probe = useCallback(async () => {
    try {
      const me = await adminApi.me();
      if (me.admin) { setUsername(me.username ?? ''); setPhase('app'); }
      else setPhase('login');
    } catch { setPhase('login'); }
  }, []);

  useEffect(() => { document.title = 'Riffloop · Admin'; probe(); }, [probe]);

  if (phase === 'loading') return <div className="adm-root"><div className="adm-loading"><div className="adm-spin" /></div></div>;
  if (phase === 'login') return <AdminLogin onAuthed={probe} />;
  return <AdminShell username={username} onExpire={() => setPhase('login')} />;
}

// ── Login ───────────────────────────────────────────────────────────────────
function AdminLogin({ onAuthed }: { onAuthed: () => void }) {
  const [theme, toggleTheme] = useAdminTheme();
  const [step, setStep] = useState<'creds' | 'code'>('creds');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function submitCreds() {
    if (!username.trim() || !password || busy) return;
    setErr(null); setInfo(null); setBusy(true);
    try {
      const r = await adminApi.login(username.trim(), password);
      setInfo(r.method === 'totp' ? 'Enter the 6-digit code from your authenticator app.'
        : r.sent === false ? (r.detail || 'Code generated, but email failed. Check mail config.')
        : 'A 6-digit code was emailed to the owner address.');
      setStep('code');
    } catch (e) {
      const a = e as ApiError;
      setErr(a.status === 429 ? a.detail : 'Invalid credentials.');
    } finally { setBusy(false); }
  }
  async function submitCode() {
    if (code.length !== 6 || busy) return;
    setErr(null); setBusy(true);
    try { await adminApi.verify(code.trim()); onAuthed(); }
    catch (e) {
      const a = e as ApiError;
      setErr(a.detail || 'Invalid code.');
      if (a.code === 'no_pending' || a.code === 'code_expired' || a.code === 'code_attempts') { setStep('creds'); setCode(''); }
    } finally { setBusy(false); }
  }

  return (
    <div className="adm-root">
      <div className="adm-login">
        <ThemeToggle theme={theme} onToggle={toggleTheme} big />
        <div className="adm-login-card">
          <div className="adm-accent-bar" />
          <div style={{ marginBottom: 22 }}><Logo size="md" /></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 6 }}>
            <span style={{ width: 9, height: 9, borderRadius: 99, background: 'var(--accent-2)', boxShadow: '0 0 12px var(--accent-2)' }} />
            <h1 style={{ margin: 0, fontSize: 21, fontWeight: 800, letterSpacing: '-.01em' }}>Admin Console</h1>
          </div>
          <p style={{ margin: '0 0 24px', color: 'var(--dim)', fontSize: 14 }}>Owner access only. All sessions are logged.</p>

          {err && <div className="adm-loginerr">{err}</div>}
          {info && step === 'code' && <div className="adm-logininfo">{info}</div>}

          {step === 'creds' ? (
            <>
              <label className="adm-login-label">Username</label>
              <input className="adm-login-input" style={{ marginBottom: 18 }} value={username} autoFocus autoComplete="username"
                onChange={(e) => setUsername(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submitCreds()} />
              <label className="adm-login-label">Password</label>
              <div style={{ position: 'relative', marginBottom: 24 }}>
                <input className="adm-login-input" style={{ paddingRight: 46 }} type={showPass ? 'text' : 'password'} value={password}
                  autoComplete="current-password" placeholder="••••••••"
                  onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submitCreds()} />
                <button onClick={() => setShowPass((s) => !s)} aria-label="Show password"
                  style={{ position: 'absolute', right: 6, top: 6, width: 36, height: 36, border: 'none', background: 'transparent', color: 'var(--faint)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 9 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" /></svg>
                </button>
              </div>
              <button className="adm-cta" disabled={!username.trim() || !password || busy} onClick={submitCreds}>{busy ? 'Checking…' : 'Continue'}</button>
            </>
          ) : (
            <>
              <label className="adm-login-label">6-digit code</label>
              <input className="adm-login-input adm-code" style={{ marginBottom: 24 }} value={code} autoFocus inputMode="numeric" maxLength={6}
                placeholder="••••••" onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} onKeyDown={(e) => e.key === 'Enter' && submitCode()} />
              <button className="adm-cta" disabled={code.length !== 6 || busy} onClick={submitCode}>{busy ? 'Verifying…' : 'Verify & sign in'}</button>
              <button onClick={() => { setStep('creds'); setErr(null); setInfo(null); setCode(''); }}
                style={{ width: '100%', marginTop: 10, background: 'transparent', border: 'none', color: 'var(--dim)', fontSize: 13, cursor: 'pointer' }}>Back</button>
            </>
          )}
          <p style={{ margin: '18px 0 0', textAlign: 'center', fontSize: 12, color: 'var(--faint)' }}>Protected area · unauthorized access prohibited</p>
        </div>
      </div>
    </div>
  );
}

// ── Shell ───────────────────────────────────────────────────────────────────
function AdminShell({ username, onExpire }: { username: string; onExpire: () => void }) {
  const [theme, toggleTheme] = useAdminTheme();
  const [nav, setNav] = useState<'overview' | 'users' | 'features' | 'splitter' | 'uninstalls' | 'reviews' | 'seo' | 'settings'>(() => {
    const p = adminUrlGet('p');
    return p === 'users' || p === 'features' || p === 'splitter' || p === 'uninstalls' || p === 'reviews' || p === 'seo' || p === 'settings' ? p : 'overview';
  });
  const [seoTab, setSeoTab] = useState<SeoTab>(() => {
    const t = adminUrlGet('t') as SeoTab | null;
    return t && SEO_TABS.some((x) => x.key === t) ? t : 'articles';
  });
  // Keep the URL in sync so a refresh restores the section (and the SeoModule
  // restores the open article from ?a=). SeoModule owns the ?a= param.
  useEffect(() => {
    const patch: Record<string, string | null> = { p: nav, t: nav === 'seo' ? seoTab : null };
    if (nav !== 'seo') patch.a = null;
    adminUrlSet(patch);
  }, [nav, seoTab]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [mode, setMode] = useState<'simple' | 'advanced'>(() => {
    // Simple is the default; only an explicit 'advanced' choice overrides it.
    try { return localStorage.getItem('rl-admin-mode') === 'advanced' ? 'advanced' : 'simple'; } catch { return 'simple'; }
  });
  const pickMode = (m: 'simple' | 'advanced') => { try { localStorage.setItem('rl-admin-mode', m); } catch { /* ignore */ } setMode(m); };

  async function logout() { try { await adminApi.logout(); } catch { /* ignore */ } onExpire(); }

  const NAV: { key: 'overview' | 'users' | 'features' | 'splitter' | 'uninstalls' | 'reviews' | 'seo' | 'settings'; label: string; icon: ReactNode }[] = [
    { key: 'overview', label: 'Overview', icon: (<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="9" rx="1" /><rect x="14" y="3" width="7" height="5" rx="1" /><rect x="14" y="12" width="7" height="9" rx="1" /><rect x="3" y="16" width="7" height="5" rx="1" /></svg>) },
    { key: 'users', label: 'Users', icon: (<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /></svg>) },
    { key: 'features', label: 'Features', icon: (<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><path d="M7 15l4-5 3 3 5-7" /></svg>) },
    { key: 'splitter', label: 'Splitter', icon: (<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16" /><path d="M6 12h12" /><path d="M9 18h6" /></svg>) },
    { key: 'uninstalls', label: 'Uninstalls', icon: (<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M8 10l4 4 4-4" /><path d="M12 3v11" /></svg>) },
    { key: 'reviews', label: 'Reviews', icon: (<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" /></svg>) },
    { key: 'seo', label: 'SEO & Articles', icon: (<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>) },
    { key: 'settings', label: 'Settings', icon: (<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>) },
  ];
  const title = nav === 'overview' ? 'Overview' : nav === 'users' ? 'Users' : nav === 'features' ? 'Features' : nav === 'splitter' ? 'Splitter funnel' : nav === 'uninstalls' ? 'Uninstalls' : nav === 'reviews' ? 'Reviews' : nav === 'seo' ? 'SEO & Articles' : 'Settings';

  return (
    <div className="adm-root">
      <div className="adm-shell">
        <aside className="adm-side">
          <div className="adm-side-brand"><Logo size="sm" /></div>

          <div className="adm-side-label">CONSOLE</div>
          <nav className="adm-side-nav">
            {NAV.map((n) => (
              <button key={n.key} className={`adm-navlink ${nav === n.key ? 'on' : ''}`} onClick={() => setNav(n.key)}>
                {n.icon}<span>{n.label}</span>
              </button>
            ))}
          </nav>

          {nav === 'settings' && (
            <div className="adm-desk-only">
              <div className="adm-side-label">SECTIONS</div>
              <nav className="adm-side-sub">
                <a href="#sec-checkout">Checkout mode</a>
                <a href="#sec-pricing">Plans &amp; pricing</a>
                <a href="#sec-paypro">PayPro config</a>
              </nav>
            </div>
          )}

          {nav === 'seo' && (
            <div className="adm-desk-only">
              <div className="adm-side-label">SEO SECTIONS</div>
              <nav className="adm-side-sub">
                {SEO_TABS.map((t) => (
                  <button key={t.key} className={seoTab === t.key ? 'on' : ''} onClick={() => setSeoTab(t.key)}>
                    <span style={{ width: 18, textAlign: 'center' }}>{t.icon}</span>{t.label}
                  </button>
                ))}
              </nav>
            </div>
          )}

          <div className="adm-side-spacer" />

          <div className="adm-side-foot">
            <div className="adm-side-user adm-desk-only">
              <span className="adm-side-ava">{(username || 'a').slice(0, 1).toUpperCase()}</span>
              <div className="adm-side-userinfo">
                <div className="nm">{username || 'admin'}</div>
                <div className="rl">admin</div>
              </div>
            </div>
            <div className="adm-side-actions">
              <ThemeToggle theme={theme} onToggle={toggleTheme} />
              <button className="adm-side-signout" onClick={logout} aria-label="Sign out">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" /></svg>
                <span>Sign out</span>
              </button>
            </div>
          </div>
        </aside>

        <div className="adm-main2">
          <div className="adm-topbar2">
            <div>
              <div className="adm-topbar2-eyebrow">ADMIN / {title.toUpperCase()}</div>
              <div className="adm-topbar2-title">{title}</div>
            </div>
            {nav !== 'settings' && nav !== 'seo' && (
              <div className="adm-viewseg">
                <button className={mode === 'simple' ? 'on' : ''} title="Only the essentials" onClick={() => pickMode('simple')}>Simple</button>
                <button className={mode === 'advanced' ? 'on' : ''} title="Every metric" onClick={() => pickMode('advanced')}>Advanced</button>
              </div>
            )}
          </div>

          <div className="adm-main2-content">
            {nav === 'overview' && <Overview onExpire={onExpire} mode={mode} />}
            {nav === 'users' && <UsersList key={refreshKey} onOpen={setOpenId} onExpire={onExpire} mode={mode} />}
            {nav === 'features' && <Features onExpire={onExpire} />}
            {nav === 'splitter' && <SplitterFunnel onExpire={onExpire} />}
            {nav === 'uninstalls' && <UninstallsList onOpen={setOpenId} onExpire={onExpire} mode={mode} />}
            {nav === 'reviews' && <ReviewsList onOpen={setOpenId} onExpire={onExpire} mode={mode} />}
            {nav === 'seo' && <SeoModule tab={seoTab} setTab={setSeoTab} onExpire={onExpire} />}
            {nav === 'settings' && <SettingsTab onExpire={onExpire} />}
          </div>
        </div>
      </div>

      {openId && (
        <UserDrawer id={openId} onClose={() => setOpenId(null)} onExpire={onExpire}
          onChanged={() => setRefreshKey((k) => k + 1)} />
      )}
    </div>
  );
}

// ── Overview ────────────────────────────────────────────────────────────────
function Overview({ onExpire, mode }: { onExpire: () => void; mode: 'simple' | 'advanced' }) {
  const simple = mode === 'simple';
  const [d, setD] = useState<AdminOverview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    adminApi.overview().then(setD).catch((e) => { if (isExpired(e)) onExpire(); else setErr('Could not load overview.'); });
  }, [onExpire]);
  const signupEmails = (d?.recent_signups || []).map((s) => s.email);
  const gravatars = useGravatars(signupEmails);

  if (err) return <div className="adm-loginerr">{err}</div>;
  if (!d) return <div className="adm-loading"><div className="adm-spin" /></div>;

  const daily = d.separations.daily.map((x) => x.c);
  const sep30 = daily.reduce((a, b) => a + b, 0);
  const sp = spark(daily.length ? daily : [0, 0], 300, 90, 8);
  const total = Math.max(1, d.plans.free + d.plans.paid);
  const segs = [
    { label: 'Free', color: 'var(--faint)', n: d.plans.free },
    { label: 'Pro', color: 'var(--accent)', n: d.plans.pro },
    { label: 'Lifetime', color: 'var(--lifetime)', n: d.plans.lifetime },
  ];
  const stickiness = d.active.mau > 0 ? Math.round((d.active.dau / d.active.mau) * 100) : 0;

  const kpis = [
    { label: 'TOTAL USERS', value: d.users.total.toLocaleString(), sub: `+${d.users.new7} this week`, color: 'var(--text)' },
    { label: 'PAID', value: d.plans.paid.toLocaleString(), sub: `${d.conversion}% conversion`, color: 'var(--accent)' },
    { label: 'MRR', value: fmtMoney(d.mrr), sub: d.arr >= 1000 ? `$${(d.arr / 1000).toFixed(1)}k ARR` : `${fmtMoney(d.arr)} ARR`, color: 'var(--accent)' },
    { label: 'PRO (SUB)', value: d.plans.pro.toLocaleString(), sub: `${d.plans.monthly} mo · ${d.plans.annual} yr`, color: 'var(--text)' },
    { label: 'LIFETIME', value: d.plans.lifetime.toLocaleString(), sub: `${fmtMoney(d.lifetime_revenue)} one-time`, color: 'var(--text)' },
    { label: 'FREE', value: d.plans.free.toLocaleString(), sub: `${Math.round((d.plans.free / total) * 100)}% of base`, color: 'var(--text)' },
  ];
  const active = [
    { label: 'DAU', value: d.active.dau, sub: 'active today' },
    { label: 'WAU', value: d.active.wau, sub: 'last 7 days' },
    { label: 'MAU', value: d.active.mau, sub: 'last 30 days' },
    { label: 'LOGINS 7D', value: d.active.login7, sub: 'sessions' },
    { label: 'LOGINS 30D', value: d.active.login30, sub: 'sessions' },
    { label: 'NEW 30D', value: d.users.new30, sub: 'signups' },
  ];
  const engagement = [
    { k: 'Conversion', v: `${d.conversion}%`, c: 'var(--green)' },
    { k: 'Stickiness · DAU/MAU', v: `${stickiness}%`, c: 'var(--text)' },
    { k: 'New · 30d', v: d.users.new30.toLocaleString(), c: 'var(--accent)' },
  ];
  // Simple-mode "Last 7 days" must-haves (all real).
  const weekStats = [
    { label: 'NEW SIGNUPS', value: d.users.new7, sub: 'last 7 days', color: 'var(--text)' },
    { label: 'SEPARATIONS', value: d.separations.last7, sub: 'last 7 days', color: 'var(--accent)' },
    { label: 'ACTIVE TODAY', value: d.active.dau, sub: 'separated today', color: 'var(--accent)' },
    { label: 'LOGINS 7D', value: d.active.login7, sub: 'sessions', color: 'var(--text)' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
      <div className="adm-statgrid">
        {(simple ? kpis.slice(0, 3) : kpis).map((k) => (
          <div className="adm-card adm-stat" key={k.label}>
            <div className="adm-stat-k">{k.label}</div>
            <div className="adm-stat-v" style={{ color: k.color }}>{k.value}</div>
            <div className="adm-stat-sub">{k.sub}</div>
          </div>
        ))}
      </div>

      {simple && (
        <>
          <div>
            <div className="adm-section-label">Last 7 days</div>
            <div className="adm-statgrid">
              {weekStats.map((w) => (
                <div className="adm-card adm-stat" key={w.label}>
                  <div className="adm-stat-k">{w.label}</div>
                  <div className="adm-stat-v sm" style={{ color: w.color }}>{w.value.toLocaleString()}</div>
                  <div className="adm-stat-sub">{w.sub}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '13px 16px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 13, color: 'var(--dim)', fontSize: 13 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="9" /><path d="M12 8h.01M11 12h1v4h1" /></svg>
            <span>Showing the essentials. Switch to <strong style={{ color: 'var(--text)', fontWeight: 700 }}>Advanced</strong> for revenue, engagement, separations &amp; activity.</span>
          </div>
        </>
      )}

      {!simple && (<>
      <div className="adm-row3">
        {/* Separations trend */}
        <div className="adm-card adm-panel">
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
            <div>
              <div className="adm-panel-h">Separations · 30 days</div>
              <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-.02em', marginTop: 6, fontVariantNumeric: 'tabular-nums' }}>{sep30.toLocaleString()}</div>
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, padding: '5px 9px', borderRadius: 8, color: 'var(--green)', background: 'var(--surface-3)' }}>+{d.separations.last7} this wk</span>
          </div>
          <svg viewBox="0 0 300 90" preserveAspectRatio="none" style={{ width: '100%', height: 92, marginTop: 14, display: 'block' }}>
            <defs>
              <linearGradient id="admRevFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#7c5cff" stopOpacity="0.34" /><stop offset="100%" stopColor="#7c5cff" stopOpacity="0" /></linearGradient>
              <linearGradient id="admRevStroke" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#18c0ff" /><stop offset="55%" stopColor="#4f7bff" /><stop offset="100%" stopColor="#b14bff" /></linearGradient>
            </defs>
            <path d={sp.area} fill="url(#admRevFill)" />
            <path d={sp.line} fill="none" stroke="url(#admRevStroke)" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
          </svg>
          <div style={{ fontSize: 12, color: 'var(--faint)', marginTop: 8 }}>{d.separations.total.toLocaleString()} all-time · daily counts</div>
        </div>

        {/* Plan mix */}
        <div className="adm-card adm-panel">
          <div className="adm-panel-h" style={{ marginBottom: 16 }}>Plan mix</div>
          <div style={{ display: 'flex', height: 14, borderRadius: 99, overflow: 'hidden', background: 'var(--surface-3)' }}>
            {segs.map((s) => <div key={s.label} style={{ width: `${(s.n / total) * 100}%`, background: s.color }} />)}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 11, marginTop: 18 }}>
            {segs.map((s) => (
              <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color, flexShrink: 0 }} />
                <span style={{ fontWeight: 600 }}>{s.label}</span>
                <span style={{ flex: 1 }} />
                <span style={{ color: 'var(--dim)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{s.n.toLocaleString()} · {Math.round((s.n / total) * 100)}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Engagement */}
        <div className="adm-card adm-panel">
          <div className="adm-panel-h" style={{ marginBottom: 6 }}>Engagement</div>
          {engagement.map((e, i) => (
            <div key={e.k} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 0', borderBottom: i < engagement.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <span style={{ fontSize: 14, color: 'var(--dim)' }}>{e.k}</span>
              <span style={{ fontSize: 18, fontWeight: 800, color: e.c, fontVariantNumeric: 'tabular-nums' }}>{e.v}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="adm-section-label">Active users</div>
        <div className="adm-statgrid">
          {active.map((a) => (
            <div className="adm-card adm-stat" key={a.label}>
              <div className="adm-stat-k">{a.label}</div>
              <div className="adm-stat-v sm">{a.value.toLocaleString()}</div>
              <div className="adm-stat-sub">{a.sub}</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="adm-section-label">Separations · last 30 days <span className="muted">({d.separations.last7} this week · {d.separations.total.toLocaleString()} all-time)</span></div>
        {d.recent.length === 0 ? (
          <div className="adm-empty">No separations logged yet. Counts populate as users separate tracks in the Studio.</div>
        ) : (
          <div className="adm-feed">
            {d.recent.map((s, i) => (
              <div className="adm-feed-row" key={i}>
                <span className="adm-feed-ic"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 12h2.5M9 6v12M13.5 9v6M18 4v16M22 11h-2.5" /></svg></span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="adm-ellip" style={{ fontSize: 14, fontWeight: 600 }}>{s.video_id || 'Untitled'}</div>
                  <div className="adm-ellip" style={{ fontSize: 12, color: 'var(--faint)' }}>{stemLabel(s.stem)} · {s.source_kind || 'youtube'}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div className="adm-ellip" style={{ fontSize: 13, color: 'var(--dim)', maxWidth: 140 }}>{s.name || s.email || '—'}</div>
                  <div style={{ fontSize: 12, color: 'var(--faint)' }}>{rel(s.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="adm-row2">
        <div>
          <div className="adm-section-label">Top tracks</div>
          <div className="adm-feed">
            {d.top_tracks.length === 0 ? <div style={{ padding: 26, textAlign: 'center', color: 'var(--faint)', fontSize: 14 }}>No tracks yet.</div> : (
              d.top_tracks.map((t, i) => (
                <div className="adm-feed-row" key={t.video_id + i}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--faint)', width: 18, fontVariantNumeric: 'tabular-nums' }}>{i + 1}</span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="adm-ellip" style={{ fontSize: 14, fontWeight: 600 }}>{t.video_id}</div>
                    <div style={{ fontSize: 12, color: 'var(--faint)' }}>{t.source_kind || 'youtube'}</div>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--dim)', flexShrink: 0 }}>{t.c} {t.c === 1 ? 'separation' : 'separations'}</span>
                </div>
              ))
            )}
          </div>
        </div>
        <div>
          <div className="adm-section-label">Recent signups</div>
          <div className="adm-feed">
            {d.recent_signups.length === 0 ? <div style={{ padding: 26, textAlign: 'center', color: 'var(--faint)', fontSize: 14 }}>No signups yet.</div> : (
              d.recent_signups.map((u) => (
                <div className="adm-feed-row" key={u.id}>
                  <Avatar name={u.name} email={u.email} gravatar={gravatars[u.email]} size={32} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="adm-ellip" style={{ fontSize: 14, fontWeight: 600 }}>{u.name || u.email}</div>
                    <div className="adm-ellip" style={{ fontSize: 12, color: 'var(--faint)' }}>{u.email}</div>
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--faint)', flexShrink: 0 }}>{rel(u.created_at)}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      </>)}
    </div>
  );
}

// ── Users list ──────────────────────────────────────────────────────────────
const PLAN_FILTERS: ('all' | 'pro' | 'lifetime' | 'free')[] = ['all', 'pro', 'lifetime', 'free'];

// ── Acquisition labels ──────────────────────────────────────────────────────
// Server sends coarse keys; anything unmapped (including 'ref:<host>', which
// carries the host itself) falls through to a readable form rather than being
// hidden, so a new referrer never silently disappears from the admin.
const SOURCE_LABEL: Record<string, string> = {
  chatgpt: 'ChatGPT', claude: 'Claude', perplexity: 'Perplexity', gemini: 'Gemini',
  copilot: 'Copilot', google: 'Google', bing: 'Bing', duckduckgo: 'DuckDuckGo',
  yandex: 'Yandex', chrome_store: 'Chrome Store', youtube: 'YouTube', reddit: 'Reddit',
  facebook: 'Facebook', instagram: 'Instagram', tiktok: 'TikTok', x: 'X',
  direct: 'Direct', other: 'Other', unknown: 'Unknown',
};
function sourceText(s?: string | null): string {
  if (!s) return 'Unknown';
  if (s.startsWith('ref:')) return s.slice(4);
  return SOURCE_LABEL[s] || s;
}
// AI assistants get their own tint — that channel is the one worth spotting.
function sourceTone(s?: string | null): string {
  if (!s || s === 'unknown') return 'var(--faint)';
  if (['chatgpt', 'claude', 'perplexity', 'gemini', 'copilot'].includes(s)) return '#7c5cff';
  if (['google', 'bing', 'duckduckgo', 'yandex'].includes(s)) return '#2f9e6e';
  if (s === 'chrome_store') return '#d98a1f';
  return 'var(--dim)';
}
// The first page they ever landed on. '/' reads as "Home"; the trailing slash is
// dropped so the column stays narrow. Empty for accounts with no recorded landing
// (direct-to-Studio arrivals, and anything older than the log window).
function landingText(p?: string | null): string {
  if (!p) return '—';
  if (p === '/' || p === '') return 'Home';
  return p.replace(/\/$/, '');
}
const DEVICE_LABEL: Record<string, string> = { mobile: 'Mobile', tablet: 'Tablet', desktop: 'Desktop', unknown: 'Unknown' };
// A native <select> on purpose: the option list is data-driven (a new referrer
// can appear at any time) and this keeps keyboard/mobile behaviour for free.
// Counts come from the server's facets; "All" shows the grand total.
function FacetSelect({ label, value, items, fmt, onChange }: {
  label: string;
  value: string;
  items?: { key: string; n: number }[];
  fmt: (k: string) => string;
  onChange: (v: string) => void;
}) {
  const list = items || [];
  const total = list.reduce((a, b) => a + b.n, 0);
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--dim)' }}>
      <span style={{ color: 'var(--faint)' }}>{label}</span>
      <select
        className="adm-chip"
        style={{ padding: '6px 10px', cursor: 'pointer', maxWidth: 190 }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="all">All{total ? ` (${total})` : ''}</option>
        {list.map((it) => (
          <option key={it.key} value={it.key}>{fmt(it.key)} ({it.n})</option>
        ))}
      </select>
    </label>
  );
}

// ── Which surface they actually use ─────────────────────────────────────────
// Read from the separation log, NOT from signup_surface: where an account was
// created says nothing about where it later did its work. 'none' = never ran a
// separation; 'unknown' = every one of their separations predates the extension
// reporting a surface (all such rows are older than 2026-07-07). 'android' = ran at
// least one in the Android app (2026-09-13); it wins over the web buckets, so every
// account that never used the app reads exactly as before.
const USES_META: Record<string, { label: string; color: string }> = {
  android:   { label: 'Android',   color: 'var(--green)' },
  extension: { label: 'Extension', color: 'var(--amber)' },
  studio:    { label: 'Studio',    color: 'var(--lifetime)' },
  both:      { label: 'Both',      color: 'var(--accent)' },
  unknown:   { label: 'Unknown',   color: 'var(--faint)' },
  none:      { label: '—',         color: 'var(--faint)' },
};
// The dropdown says what the filter DOES; the table tag says what the user IS.
// "Extension" in a row means extension-and-not-studio, which as a filter reads
// better spelled out.
const USES_FILTER_LABEL: Record<string, string> = {
  all: 'All', android: 'Android app', extension: 'Extension only', studio: 'Studio only',
  both: 'Both', unknown: 'Unknown', none: 'Never separated',
};

// Puzzle piece = browser extension (the same glyph Chrome puts in its toolbar).
function ExtIcon({ size = 12 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 3.5a2 2 0 1 0-4 0V6H7.5A1.5 1.5 0 0 0 6 7.5V10H3.5a2 2 0 1 0 0 4H6v2.5A1.5 1.5 0 0 0 7.5 18H10v2.5a2 2 0 1 0 4 0V18h2.5a1.5 1.5 0 0 0 1.5-1.5V14h2.5a2 2 0 1 0 0-4H18V7.5A1.5 1.5 0 0 0 16.5 6H14V3.5z" /></svg>;
}
// Mixer faders = the Studio.
function StudioIcon({ size = 12 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 21v-6M5 11V3M12 21v-9M12 8V3M19 21v-4M19 13V3M2 15h6M9 8h6M16 17h6" /></svg>;
}
// Phone = the Android app.
function PhoneIcon({ size = 12 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="7" y="2" width="10" height="20" rx="2" /><path d="M12 18h.01" /></svg>;
}

// Exact counts behind the tag, so hovering answers "how much of each".
function usesTitle(u: { separations: number; sep_ext?: number; sep_studio?: number; sep_android?: number }): string {
  if (!u.separations) return 'No separations yet';
  const e = u.sep_ext || 0, st = u.sep_studio || 0, a = u.sep_android || 0;
  const older = Math.max(0, u.separations - e - st - a);
  const parts: string[] = [];
  if (a) parts.push(`Android ${a}`);
  if (e) parts.push(`Extension ${e}`);
  if (st) parts.push(`Studio ${st}`);
  if (older) parts.push(`${older} logged before surface tracking`);
  return parts.join(' · ');
}

function UsesTag({ u, size = 12, font = 13 }: { u: { uses?: string; separations: number; sep_ext?: number; sep_studio?: number; sep_android?: number }; size?: number; font?: number }) {
  const k = u.uses || (u.separations ? 'unknown' : 'none');
  const m = USES_META[k] || USES_META.none;
  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, minWidth: 0, fontSize: font, color: m.color, fontWeight: k === 'none' || k === 'unknown' ? 400 : 600 }}
      title={usesTitle(u)}
    >
      {k === 'android' && <PhoneIcon size={size} />}
      {(k === 'extension' || k === 'both') && <ExtIcon size={size} />}
      {(k === 'studio' || k === 'both') && <StudioIcon size={size} />}
      <span className="adm-ellip">{m.label}</span>
    </span>
  );
}

function DeviceIcon({ d, size = 13 }: { d?: string | null; size?: number }) {
  if (!d || d === 'unknown') return null;
  const t = DEVICE_LABEL[d] || d;
  if (d === 'desktop') {
    return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><title>{t}</title><rect x="2" y="4" width="20" height="13" rx="2" /><path d="M8 21h8M12 17v4" /></svg>;
  }
  // mobile + tablet share the slab; tablet is just wider.
  const w = d === 'tablet' ? 14 : 10;
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><title>{t}</title><rect x={(24 - w) / 2} y="2" width={w} height="20" rx="2" /><path d="M12 18h.01" /></svg>;
}

function UsersList({ onOpen, onExpire, mode }: { onOpen: (id: string) => void; onExpire: () => void; mode: 'simple' | 'advanced' }) {
  const simple = mode === 'simple';
  // Advanced gains Uses + Source + Device as their own columns.
  // Widths re-balanced when Uses was added so the extra column costs ~20px of
  // total width instead of 116 — every other column gave back the slack it had.
  const cols = simple ? 'minmax(190px,1.7fr) 120px minmax(110px,1fr) 44px' : 'minmax(170px,1.4fr) 82px 88px 104px 132px 92px 92px 78px 92px 88px 44px';
  const minW = simple ? 420 : 1202;
  const [rows, setRows] = useState<AdminUserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [qLive, setQLive] = useState('');
  const [q, setQ] = useState('');
  const [plan, setPlan] = useState<'all' | 'pro' | 'lifetime' | 'free'>('all');
  const [source, setSource] = useState('all');
  const [device, setDevice] = useState('all');
  const [uses, setUses] = useState('all');
  const [facets, setFacets] = useState<AdminUsersResp['facets'] | undefined>(undefined);
  const [sort, setSort] = useState('last_login');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const per = 25;

  useEffect(() => { const t = setTimeout(() => { setQ(qLive); setPage(1); }, 300); return () => clearTimeout(t); }, [qLive]);
  useEffect(() => {
    setLoading(true);
    adminApi.users({ q, plan, source, device, uses, sort, dir, page, per })
      .then((r) => { setRows(r.users); setTotal(r.total); if (r.facets) setFacets(r.facets); })
      .catch((e) => { if (isExpired(e)) onExpire(); })
      .finally(() => setLoading(false));
  }, [q, plan, source, device, uses, sort, dir, page, onExpire]);

  const gravatars = useGravatars(rows.map((r) => r.email));
  const pages = Math.max(1, Math.ceil(total / per));

  async function exportAll() {
    try {
      const r = await adminApi.users({ q, plan, source, device, uses, sort, dir, page: 1, per: 1000 });
      const head = ['Name', 'Email', 'Sign-in', 'Activated', 'Plan', 'Status', 'Source', 'Landed on', 'Signup device', 'Signup surface', 'Last device', 'Uses', 'Separations', 'Ext separations', 'Studio separations', 'Android separations', 'Sections', 'Joined', 'Last seen', 'User ID'];
      const body = r.users.map((u) => [u.name || '', u.email, authMethodLabel(u.auth_method), u.activated === false ? 'no' : 'yes', planText(u.plan), u.status, sourceText(u.signup_source), u.signup_landing || '', DEVICE_LABEL[u.signup_device || 'unknown'] || '', u.signup_surface || '', DEVICE_LABEL[u.last_device || 'unknown'] || '', u.uses || '', u.separations, u.sep_ext ?? '', u.sep_studio ?? '', u.sep_android ?? '', u.sections_count, u.created_at || '', u.last_login_at || '', u.id]);
      downloadCsv('riffloop-users.csv', [head, ...body]);
    } catch (e) { if (isExpired(e)) onExpire(); }
  }

  function Sortable({ k, label }: { k: string; label: string }) {
    const on = sort === k;
    return <button onClick={() => { if (on) setDir(dir === 'asc' ? 'desc' : 'asc'); else { setSort(k); setDir('desc'); } }}>{label}{on ? (dir === 'asc' ? ' ↑' : ' ↓') : ''}</button>;
  }

  function row(u: AdminUserRow) {
    const pr = presence(u.last_login_at);
    return { u, pr };
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--faint)' }}><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>
          <input className="adm-input" style={{ paddingLeft: 42 }} placeholder="Search name or email…" value={qLive} onChange={(e) => setQLive(e.target.value)} />
        </div>
        <span style={{ fontSize: 14, color: 'var(--dim)', fontWeight: 600, whiteSpace: 'nowrap' }}>{total} user{total === 1 ? '' : 's'}</span>
        <button className="adm-btn" onClick={exportAll}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12M7 10l5 5 5-5M5 21h14" /></svg>Export
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {PLAN_FILTERS.map((f) => (
          <button key={f} className={`adm-chip ${plan === f ? 'on' : ''}`} onClick={() => { setPlan(f); setPage(1); }}>
            {f === 'all' ? 'All plans' : planText(f as PlanLabel)}
          </button>
        ))}
        {/* Acquisition filters. Options come from server-side facet counts over
            ALL users, so the totals stay stable while you switch between them.
            Accounts created before 2026-08-28 group under "Unknown". */}
        <FacetSelect
          label="Source" value={source} items={facets?.source}
          fmt={sourceText} onChange={(v) => { setSource(v); setPage(1); }}
        />
        <FacetSelect
          label="Device" value={device} items={facets?.device}
          fmt={(k) => DEVICE_LABEL[k] || k} onChange={(v) => { setDevice(v); setPage(1); }}
        />
        {/* Android app / Extension / Studio / both. Counts come from the same facet
            pass, so the buckets always add up to the user total. */}
        <FacetSelect
          label="Uses" value={uses} items={facets?.uses}
          fmt={(k) => USES_FILTER_LABEL[k] || k} onChange={(v) => { setUses(v); setPage(1); }}
        />
        {(source !== 'all' || device !== 'all' || uses !== 'all') && (
          <button className="adm-chip" onClick={() => { setSource('all'); setDevice('all'); setUses('all'); setPage(1); }}>Clear</button>
        )}
      </div>

      {/* Desktop table */}
      <div className="adm-table">
        <div className="adm-table-min" style={{ minWidth: minW }}>
          <div className="adm-tr adm-th" style={{ gridTemplateColumns: cols }}>
            <div><Sortable k="email" label="User" /></div>
            <div>Plan</div>
            {!simple && <><div>Uses</div>
            <div>Source</div>
            <div>Landed on</div>
            <div>Device</div>
            <div><Sortable k="separations" label="Separations" /></div>
            <div><Sortable k="sections" label="Sections" /></div>
            <div><Sortable k="created" label="Joined" /></div></>}
            <div><Sortable k="last_login" label="Last seen" /></div>
            <div />
          </div>
          {rows.map((u) => {
            const { pr } = row(u);
            return (
              <div className="adm-tr adm-trow" key={u.id} onClick={() => onOpen(u.id)} style={{ gridTemplateColumns: cols }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
                  <Avatar name={u.name} email={u.email} picture={u.picture_url} gravatar={gravatars[u.email]} size={36} />
                  <div style={{ minWidth: 0 }}>
                    <div className="adm-ellip" style={{ fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 7 }}>{u.name || u.email}<span style={{ width: 7, height: 7, borderRadius: 99, background: pr.color, flexShrink: 0 }} title={pr.label} /></div>
                    <div className="adm-ellip" style={{ fontSize: 12, color: 'var(--faint)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span className="adm-ellip">{u.email}</span>
                      <AuthMethodTag method={u.auth_method} activated={u.activated} />
                    </div>
                  </div>
                </div>
                <div><span className={`adm-badge ${u.plan}`}>{planText(u.plan)}</span></div>
                {/* Extension, Studio, or both — from real separations, so it says
                    what they DO, not where they signed up. */}
                {!simple && <><div style={{ minWidth: 0 }}><UsesTag u={u} /></div>
                <div style={{ minWidth: 0, fontSize: 13, color: sourceTone(u.signup_source) }}>
                  <span className="adm-ellip" title={`${sourceText(u.signup_source)}${u.signup_surface && u.signup_surface !== 'unknown' ? ' · signed up via ' + u.signup_surface : ''}`}>{sourceText(u.signup_source)}</span>
                </div>
                {/* First page of their first visit — which content actually earns accounts. */}
                <div style={{ minWidth: 0, fontSize: 12.5, color: u.signup_landing ? 'var(--dim)' : 'var(--faint)' }}>
                  <span className="adm-ellip" title={u.signup_landing || undefined}>{landingText(u.signup_landing)}</span>
                </div>
                {/* Device at signup. When they have since moved to another device the
                    current one is appended, because "signed up on mobile, now on
                    desktop" is the interesting case. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, fontSize: 13, color: u.signup_device && u.signup_device !== 'unknown' ? 'var(--dim)' : 'var(--faint)' }}>
                  <span style={{ flexShrink: 0, display: 'flex' }}><DeviceIcon d={u.signup_device} /></span>
                  <span className="adm-ellip" title={u.last_device && u.last_device !== 'unknown' && u.last_device !== u.signup_device ? `Signed up on ${DEVICE_LABEL[u.signup_device || 'unknown']}, now on ${DEVICE_LABEL[u.last_device]}` : undefined}>
                    {DEVICE_LABEL[u.signup_device || 'unknown'] || '—'}
                    {u.last_device && u.last_device !== 'unknown' && u.last_device !== u.signup_device && (
                      <span style={{ color: 'var(--faint)' }}> → {DEVICE_LABEL[u.last_device]}</span>
                    )}
                  </span>
                </div>
                <div style={{ fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>{u.separations.toLocaleString()}</div>
                <div style={{ fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>{u.sections_count}</div>
                <div style={{ fontSize: 13, color: 'var(--dim)' }}>{fmtDate(u.created_at)}</div></>}
                <div style={{ fontSize: 13, color: 'var(--dim)' }}>{rel(u.last_login_at)}</div>
                <div style={{ color: 'var(--faint)', display: 'flex', justifyContent: 'flex-end' }}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg></div>
              </div>
            );
          })}
          {!loading && rows.length === 0 && <div style={{ padding: 46, textAlign: 'center', color: 'var(--faint)', fontSize: 14 }}>No users found.</div>}
        </div>
      </div>

      {/* Mobile cards */}
      <div className="adm-ucards">
        {rows.map((u) => {
          const { pr } = row(u);
          return (
            <div className="adm-ucard" key={u.id} onClick={() => onOpen(u.id)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                <Avatar name={u.name} email={u.email} picture={u.picture_url} gravatar={gravatars[u.email]} size={42} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="adm-ellip" style={{ fontSize: 15, fontWeight: 600 }}>{u.name || u.email}</div>
                  <div className="adm-ellip" style={{ fontSize: 12, color: 'var(--faint)' }}>{u.email}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
                    <AuthMethodTag method={u.auth_method} activated={u.activated} />
                    {u.uses && u.uses !== 'none' && <UsesTag u={u} size={11} font={11} />}
                    {u.signup_source && u.signup_source !== 'unknown' && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: sourceTone(u.signup_source) }}>
                        {sourceText(u.signup_source)}
                      </span>
                    )}
                    {u.signup_device && u.signup_device !== 'unknown' && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--faint)' }}>
                        <DeviceIcon d={u.signup_device} size={11} />{DEVICE_LABEL[u.signup_device]}
                      </span>
                    )}
                    {u.signup_landing && (
                      <span className="adm-ellip" style={{ fontSize: 11, color: 'var(--faint)', maxWidth: 150 }}>{landingText(u.signup_landing)}</span>
                    )}
                  </div>
                </div>
                <span className={`adm-badge ${u.plan}`} style={{ flexShrink: 0 }}>{planText(u.plan)}</span>
              </div>
              <div style={{ display: 'flex', gap: 18, marginTop: 13, paddingTop: 13, borderTop: '1px solid var(--border)', fontSize: 12 }}>
                <div><span style={{ color: 'var(--faint)' }}>Sep</span> <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{u.separations}</span></div>
                <div><span style={{ color: 'var(--faint)' }}>Sec</span> <span style={{ fontWeight: 700 }}>{u.sections_count}</span></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 7, height: 7, borderRadius: 99, background: pr.color }} /><span style={{ color: 'var(--dim)' }}>{pr.label}</span></div>
                <div style={{ flex: 1, textAlign: 'right', color: 'var(--faint)' }}>{rel(u.last_login_at)}</div>
              </div>
            </div>
          );
        })}
        {!loading && rows.length === 0 && <div className="adm-empty">No users found.</div>}
      </div>

      <div className="adm-pager">
        <span style={{ fontSize: 13, color: 'var(--faint)' }}>Page {Math.min(page, pages)} of {pages}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      </div>
    </div>
  );
}

// ── Features: is anyone actually USING what we shipped? ─────────────────────
// /overview answers "who signed up" and "how many separations". It has never
// been able to answer "did anyone open the lyrics sheet", because those events
// only ever went to GA4. Read-only; every number here is either from the new
// feature_events table or computed from the existing separations log.
const FEATURE_GROUPS: { title: string; note: string; events: string[] }[] = [
  { title: 'Lyrics', note: 'The 1.1.3 surface — did they open it, did the lookup land, did they have to fix the sync by hand?',
    events: ['rl_lyrics_open', 'rl_lyrics_lookup', 'rl_lyrics_align', 'rl_lyrics_sync_toggle', 'rl_lyrics_tap_sync', 'rl_lyrics_size', 'rl_lyrics_overlay', 'rl_lyrics_hint', 'rl_lyrics_use_found', 'rl_lyrics_backfill', 'rl_lyrics_upgrade', 'rl_lyrics_action', 'rl_lyrics_saved', 'rl_lyrics_cleared'] },
  { title: 'Chords & key', note: 'Roughly 132 MB of models is downloaded per browser for this.',
    events: ['rl_chords_ready', 'rl_chord_shapes_toggle', 'rl_chord_seek', 'rl_chord_style', 'rl_chord_lock_back'] },
  { title: 'Instant karaoke', note: 'Spends no credit, so it leaves no trace in the separations log at all.',
    events: ['rl_vocalcut_on', 'rl_vocalcut_off'] },
  { title: 'Upgrade pressure', note: 'The moments a paywall was actually felt, the plan picked on the offer, and how each offer was closed.',
    events: ['rl_wall_hit', 'rl_upgrade_modal_open', 'rl_upgrade_click', 'rl_plan_pick', 'rl_upgrade_modal_close', 'rl_limit_modal_close', 'rl_cap_banner_dismiss', 'rl_cap_toast_dismiss'] },
  { title: 'Export', note: 'Gate shown vs dismissed vs upgrade-clicked, and how many files actually leave. "Preview" = a trimmed free export, "Done" = a full one.',
    events: ['rl_export_gate_shown', 'rl_export_gate_dismissed', 'rl_export_upgrade_click',
             'rl_export_preview', 'rl_export_done', 'rl_export_failed'] },
  { title: 'Core flow', note: 'Denominators for everything above.',
    events: ['rl_separate', 'rl_suggest_shown', 'rl_suggest_used', 'rl_solo_detected', 'rl_solo_jump', 'rl_open_loop'] },
  // Extension 1.2.1 / 1.2.2. Most of these also fire for signed-out users: the
  // counts include them, USERS 30D counts accounts only.
  { title: 'First run (extension)', note: 'Install and update, the bar appearing, the first loop, sign-in clicks and sign-ups, why a run failed or a stem would not play, and the backing-track "pick an instrument first" nudge.',
    events: ['rl_install', 'rl_update', 'rl_activate', 'rl_create_loop', 'rl_signin_click', 'rl_signup', 'rl_separate_error', 'rl_stem_play_error', 'rl_backing_pick_required'] },
  { title: 'Demo song (extension 1.2.2)', note: 'Seeded on install, opened on its first run, the pointer tips followed or closed, and on to their own song.',
    events: ['rl_demo_seed', 'rl_demo_open', 'rl_demo_tip', 'rl_demo_welcome_close', 'rl_demo_try_own'] },
  { title: 'Toolbar popup (extension 1.2.2)', note: 'Which card it opened on, and what was clicked.',
    events: ['rl_popup_view', 'rl_popup_show_panel', 'rl_popup_reload', 'rl_popup_demo', 'rl_popup_open_youtube', 'rl_popup_widget_toggle', 'rl_popup_engine_remove', 'rl_popup_help'] },
  { title: 'Library (extension 1.2.2)', note: 'Opened, searched, a song picked from the list or a search, and whether its loop then opened by itself.',
    events: ['rl_library_open', 'rl_library_search', 'rl_library_pick', 'rl_library_autoopen'] },
  { title: 'YouTube Music & controls (extension 1.2.2)', note: 'The Riffloop tab on YouTube Music (opened, and closed by one of its own tabs), and the Speed slider.',
    events: ['rl_ytm_tab_open', 'rl_ytm_tab_close', 'rl_speed_change'] },
  { title: 'Best quality (extension 1.2.2)', note: 'Pro only: the quality switch, the one-time setup ask, and how the download ended.',
    events: ['rl_quality_pick', 'rl_best_setup', 'rl_engine_download'] },
  // 2026-09-14, "track everything": every other action in the bar and the popup.
  // Controls pressed in bursts (faders, S/M, Key, play, seeks, shortcuts) count once per loop.
  { title: 'Mixer & practice (extension)', note: 'Presets, faders, solo and mute, Key, the metronome, play and waveform seeks, keyboard shortcuts.',
    events: ['rl_mixer_preset', 'rl_mixer_fader', 'rl_mixer_solo', 'rl_mixer_mute', 'rl_key_change', 'rl_metronome', 'rl_play_toggle', 'rl_wave_seek', 'rl_shortcut'] },
  { title: 'Loops (extension)', note: 'Goal and instrument picks, the A-B range and loop switch, new, cancel and back, rename, delete, trim, and separations cancelled or re-run.',
    events: ['rl_goal_pick', 'rl_stem_pick', 'rl_range_edit', 'rl_loop_toggle', 'rl_new_loop_open', 'rl_new_loop_cancel', 'rl_loop_back', 'rl_loop_rename', 'rl_loop_delete', 'rl_trim', 'rl_loops_scroll', 'rl_separate_cancel', 'rl_reseparate_confirm', 'rl_notice_dismiss'] },
  { title: 'Account & settings (extension)', note: 'The account menu, the Studio links, sign-out, theme and accent, the affiliate link, purchases.',
    events: ['rl_account_menu_open', 'rl_studio_open', 'rl_signout', 'rl_theme_change', 'rl_accent_change', 'rl_affiliate_click', 'rl_purchase'] },
  { title: 'Review prompt (extension)', note: 'The in-extension rating ask: shown, stars given, dismissed, sent to the store, feedback written, and the follow-up nudge.',
    events: ['rl_review_prompt_open', 'rl_review_stars', 'rl_review_dismiss', 'rl_review_store_open', 'rl_review_feedback_sent', 'rl_review_nag_open', 'rl_review_nag_skipped', 'rl_review_nag_store_open'] },
];

// 'rl_lyrics_open' → 'Lyrics open'. The raw names are the source of truth and
// stay visible on hover; the table itself should read like English.
function prettyEvent(e: string): string {
  const t = e.replace(/^rl_/, '').replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function Features({ onExpire }: { onExpire: () => void }) {
  const [d, setD] = useState<AdminFeatures | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    adminApi.features().then(setD).catch((e) => { if (isExpired(e)) onExpire(); else setErr('Could not load features.'); });
  }, [onExpire]);

  if (err) return <div className="adm-loginerr">{err}</div>;
  if (!d) return <div className="adm-loading"><div className="adm-spin" /></div>;

  const byEvent = new Map(d.events.map((r) => [r.event, r]));
  const anyEvents = d.events.some((r) => r.total > 0);
  const depth = d.depth;
  const depthTotal = Math.max(1, depth['1'] + depth['2_3'] + depth['4_10'] + depth['11_plus']);
  const depthRows = [
    { label: 'Exactly 1', n: depth['1'] },
    { label: '2-3', n: depth['2_3'] },
    { label: '4-10', n: depth['4_10'] },
    { label: '11+', n: depth['11_plus'] },
  ];

  const kpis = [
    { label: 'RETURN RATE', value: d.retention.rate === null ? '—' : d.retention.rate + '%',
      sub: d.retention.cohort > 0 ? `${d.retention.returned} of ${d.retention.cohort} came back` : 'not enough data yet',
      color: 'var(--accent)' },
    { label: 'NEVER SEPARATED', value: d.reach.never_separated.toLocaleString(),
      sub: `of ${d.reach.users.toLocaleString()} accounts`, color: 'var(--text)' },
    { label: '30s CAP HITS', value: d.walls.length_30s.toLocaleString(), sub: 'last 30 days', color: 'var(--text)' },
    { label: 'DAILY CAP HITS', value: d.walls.daily_limit.toLocaleString(), sub: 'last 30 days', color: 'var(--text)' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
      <div className="adm-statgrid">
        {kpis.map((k) => (
          <div className="adm-card adm-stat" key={k.label}>
            <div className="adm-stat-k">{k.label}</div>
            <div className="adm-stat-v" style={{ color: k.color }}>{k.value}</div>
            <div className="adm-stat-sub">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Without this, a table of zeroes reads as "nobody uses lyrics" when the
          truth may be "we started collecting this yesterday". */}
      {!anyEvents && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '13px 16px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 13, color: 'var(--dim)', fontSize: 13 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="9" /><path d="M12 8h.01M11 12h1v4h1" /></svg>
          <span>No feature events yet. These arrive only from <strong style={{ color: 'var(--text)', fontWeight: 700 }}>signed-in</strong> users on a build that mirrors them, so counts stay at zero until such a build is published. Retention and reach above are computed from existing separation history and are live now.</span>
        </div>
      )}

      <div className="adm-row3">
        <div className="adm-card adm-panel">
          <div className="adm-panel-h" style={{ marginBottom: 6 }}>Do they come back?</div>
          <div className="adm-stat-sub" style={{ marginBottom: 14 }}>{d.retention.window}</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <div className="adm-stat-v" style={{ color: 'var(--accent)' }}>{d.retention.rate === null ? '—' : d.retention.rate + '%'}</div>
            <div className="adm-stat-sub">{d.retention.returned} / {d.retention.cohort}</div>
          </div>
        </div>

        <div className="adm-card adm-panel" style={{ gridColumn: 'span 2' }}>
          <div className="adm-panel-h" style={{ marginBottom: 6 }}>How far they get</div>
          <div className="adm-stat-sub" style={{ marginBottom: 14 }}>
            Separations per account, all time. {d.reach.never_separated.toLocaleString()} more never ran one.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {depthRows.map((r) => (
              <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 66, fontSize: 12, color: 'var(--dim)' }}>{r.label}</div>
                <div style={{ flex: 1, height: 9, background: 'var(--surface-2)', borderRadius: 5, overflow: 'hidden' }}>
                  <div style={{ width: `${(r.n / depthTotal) * 100}%`, height: '100%', background: 'var(--accent)' }} />
                </div>
                <div style={{ width: 54, textAlign: 'right', fontSize: 12, color: 'var(--text)' }}>{r.n.toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {FEATURE_GROUPS.map((g) => (
        <div key={g.title}>
          <div className="adm-section-label">{g.title} <span className="muted">— {g.note}</span></div>
          <div className="adm-card" style={{ padding: 0, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ color: 'var(--dim)', fontSize: 11, letterSpacing: '.04em' }}>
                  <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600 }}>EVENT</th>
                  <th style={{ textAlign: 'right', padding: '10px 14px', fontWeight: 600 }}>24H</th>
                  <th style={{ textAlign: 'right', padding: '10px 14px', fontWeight: 600 }}>7D</th>
                  <th style={{ textAlign: 'right', padding: '10px 14px', fontWeight: 600 }}>30D</th>
                  <th style={{ textAlign: 'right', padding: '10px 14px', fontWeight: 600 }}>USERS 30D</th>
                  <th style={{ textAlign: 'right', padding: '10px 14px', fontWeight: 600 }}>ALL TIME</th>
                </tr>
              </thead>
              <tbody>
                {g.events.map((ev) => {
                  const r = byEvent.get(ev);
                  const dim = !r || r.total === 0;
                  return (
                    <tr key={ev} style={{ borderTop: '1px solid var(--border)', color: dim ? 'var(--dim)' : 'var(--text)' }}>
                      <td style={{ padding: '9px 14px' }} title={ev}>
                        {prettyEvent(ev)}
                        {/* Last 30 days split by the event's main field (RL_FEATURE_SPLITS on the server). */}
                        {d.splits?.[ev] && d.splits[ev].length > 0 && (
                          <div style={{ fontSize: 11, color: 'var(--dim)', marginTop: 3 }}>
                            {d.splits[ev].map((s) => `${s.v} ${s.n.toLocaleString()}`).join(' · ')}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '9px 14px', textAlign: 'right' }}>{r ? r.d1.toLocaleString() : 0}</td>
                      <td style={{ padding: '9px 14px', textAlign: 'right' }}>{r ? r.d7.toLocaleString() : 0}</td>
                      <td style={{ padding: '9px 14px', textAlign: 'right' }}>{r ? r.d30.toLocaleString() : 0}</td>
                      <td style={{ padding: '9px 14px', textAlign: 'right' }}>{r ? r.users30.toLocaleString() : 0}</td>
                      <td style={{ padding: '9px 14px', textAlign: 'right' }}>{r ? r.total.toLocaleString() : 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <div className="adm-stat-sub">
        Counts include signed-out extension users; USERS 30D counts accounts only. The grey line under an event splits its last 30 days by its main field.
        {d.events_since ? ` Collecting since ${d.events_since.slice(0, 10)}.` : ''}
      </div>
    </div>
  );
}

// ── Splitter: the /splitter funnel, visitor by visitor ──────────────────────
// Every step of riffloop.app tool page → /splitter → preview → upgrade message →
// plan click → checkout → payment, from our OWN event log (GA4 misses ad-block
// and EEA visitors and cannot be read from here). Anonymous visitors are
// counted by the per-browser id the page mints; a later sign-in or purchase
// on the same browser links them to the account.
const SPLITTER_RANGES: { days: number; label: string }[] = [
  { days: 1, label: '24h' }, { days: 7, label: '7 days' }, { days: 30, label: '30 days' }, { days: 0, label: 'All time' },
];
const SESSION_TIER: Record<string, string> = { anonymous: 'Guest', free: 'Free', pro: 'Pro' };

function pctOf(n: number, of: number): string {
  if (!of) return '—';
  return Math.round((n / of) * 100) + '%';
}
function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function KvList({ map, total, empty }: { map: Record<string, number>; total?: number; empty?: string }) {
  const rows = Object.entries(map);
  if (!rows.length) return <div className="adm-stat-sub">{empty || 'Nothing yet'}</div>;
  const max = Math.max(1, ...rows.map(([, n]) => n));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      {rows.map(([k, n]) => (
        <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 118, fontSize: 12, color: 'var(--dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={k}>{k}</div>
          <div style={{ flex: 1, height: 8, background: 'var(--surface-2)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: `${(n / max) * 100}%`, height: '100%', background: 'var(--accent)' }} />
          </div>
          <div style={{ width: 70, textAlign: 'right', fontSize: 12, color: 'var(--text)' }}>
            {n.toLocaleString()}{total ? <span style={{ color: 'var(--faint)' }}> · {pctOf(n, total)}</span> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function SplitterFunnel({ onExpire }: { onExpire: () => void }) {
  const [days, setDays] = useState<number>(7);
  const [d, setD] = useState<AdminSplitter | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    setD(null);
    adminApi.splitter(days).then(setD).catch((e) => { if (isExpired(e)) onExpire(); else setErr('Could not load the splitter funnel.'); });
  }, [days, onExpire]);

  const range = (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {SPLITTER_RANGES.map((r) => (
        <button
          key={r.days}
          className={`adm-chip${days === r.days ? ' on' : ''}`}
          onClick={() => setDays(r.days)}
        >{r.label}</button>
      ))}
    </div>
  );

  if (err) return <div className="adm-loginerr">{err}</div>;
  if (!d) return <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>{range}<div className="adm-loading"><div className="adm-spin" /></div></div>;

  const step = (key: string) => d.steps.find((s) => s.key === key) || { key, label: key, n: 0, visitors: 0 };
  const views = step('rl_splitter_view').visitors;
  const previews = step('rl_splitter_preview_done').visitors;
  const modal = step('rl_splitter_modal_open').visitors;
  const planClicks = step('plan_click_paid').visitors;
  const checkouts = step('rl_checkout_start').visitors;
  const purchased = step('purchased').visitors;
  const th = { textAlign: 'right' as const, padding: '9px 12px', fontWeight: 600 };
  const td = { padding: '8px 12px', textAlign: 'right' as const };

  const kpis = [
    { label: 'VISITORS', value: views.toLocaleString(), sub: 'opened /splitter', color: 'var(--text)' },
    { label: 'HEARD THE SPLIT', value: previews.toLocaleString(), sub: `${pctOf(previews, views)} of visitors`, color: 'var(--accent)' },
    { label: 'SAW THE UPGRADE MESSAGE', value: modal.toLocaleString(), sub: `${pctOf(modal, previews)} of those who heard it`, color: 'var(--text)' },
    { label: 'CLICKED A PAID PLAN', value: planClicks.toLocaleString(), sub: `${pctOf(planClicks, modal)} of message viewers`, color: 'var(--text)' },
    { label: 'REACHED CHECKOUT', value: checkouts.toLocaleString(), sub: 'signed in and minted a PayPro session', color: 'var(--text)' },
    { label: 'PURCHASED', value: purchased.toLocaleString(), sub: `${pctOf(purchased, views)} of visitors`, color: 'var(--green, var(--accent))' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        {range}
        <div className="adm-stat-sub">
          {d.since ? `Collecting since ${d.since.slice(0, 10)}` : 'No splitter events recorded yet'} · counts are unique visitors (per browser) unless marked
        </div>
      </div>

      <div className="adm-statgrid">
        {kpis.map((k) => (
          <div className="adm-card adm-stat" key={k.label}>
            <div className="adm-stat-k">{k.label}</div>
            <div className="adm-stat-v" style={{ color: k.color }}>{k.value}</div>
            <div className="adm-stat-sub">{k.sub}</div>
          </div>
        ))}
      </div>

      {!views && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '13px 16px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 13, color: 'var(--dim)', fontSize: 13 }}>
          <span>No visits in this range. Events arrive from the live /splitter page the moment it loads — if this stays empty after a deploy, check that the page's requests to /api/events/log succeed.</span>
        </div>
      )}

      <div>
        <div className="adm-section-label">The funnel <span className="muted">— each bar is unique visitors; the percentage is of the step above it</span></div>
        <div className="adm-card" style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {d.steps.map((st, i) => {
            const prev = i === 0 ? st.visitors : d.steps[i - 1].visitors;
            return (
              <div key={st.key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 230, fontSize: 13, color: 'var(--text)' }}>{st.label}</div>
                <div style={{ flex: 1, height: 12, background: 'var(--surface-2)', borderRadius: 6, overflow: 'hidden' }}>
                  <div style={{ width: `${views ? (st.visitors / views) * 100 : 0}%`, height: '100%', background: st.key === 'purchased' ? 'var(--green, var(--accent))' : 'var(--accent)', opacity: 1 - i * 0.05 }} />
                </div>
                <div style={{ width: 150, textAlign: 'right', fontSize: 12, color: 'var(--text)' }}>
                  <strong>{st.visitors.toLocaleString()}</strong>
                  <span style={{ color: 'var(--faint)' }}> · {i === 0 ? '100%' : pctOf(st.visitors, prev)} · {st.n.toLocaleString()} events</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="adm-row3">
        <div className="adm-card adm-panel">
          <div className="adm-panel-h" style={{ marginBottom: 10 }}>Who came</div>
          <div className="adm-stat-sub" style={{ marginBottom: 6 }}>By plan</div>
          <KvList map={d.views.by_tier} total={views} />
          <div className="adm-stat-sub" style={{ margin: '14px 0 6px' }}>By device</div>
          <KvList map={d.views.by_device} total={views} />
        </div>
        <div className="adm-card adm-panel">
          <div className="adm-panel-h" style={{ marginBottom: 10 }}>Where from</div>
          <div className="adm-stat-sub" style={{ marginBottom: 6 }}>File sent by a tool page (handoff) vs dropped here</div>
          <KvList map={d.views.by_source} total={views} />
          <div className="adm-stat-sub" style={{ margin: '14px 0 6px' }}>Referring site</div>
          <KvList map={d.views.by_ref} total={views} />
        </div>
        <div className="adm-card adm-panel">
          <div className="adm-panel-h" style={{ marginBottom: 10 }}>The wait</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <div className="adm-stat-v" style={{ color: 'var(--accent)' }}>{d.waits.median_secs == null ? '—' : Math.round(d.waits.median_secs) + 's'}</div>
            <div className="adm-stat-sub">median to a ready preview · avg {d.waits.avg_secs == null ? '—' : Math.round(d.waits.avg_secs) + 's'}</div>
          </div>
          <div className="adm-stat-sub" style={{ marginTop: 10 }}>
            Engine already cached: {d.waits.warm} ({d.waits.warm_avg == null ? '—' : Math.round(d.waits.warm_avg) + 's avg'}) · cold download: {d.waits.cold} ({d.waits.cold_avg == null ? '—' : Math.round(d.waits.cold_avg) + 's avg'})
          </div>
          <div className="adm-stat-sub" style={{ marginTop: 10 }}>
            Cancelled the wait: {d.cancels.n}{d.cancels.n ? ` (at ${d.cancels.avg_pct}% on average)` : ''}
          </div>
          <div className="adm-stat-sub" style={{ margin: '14px 0 6px' }}>Left the page while…</div>
          {d.leaves.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
              {d.leaves.map((l) => (
                <div key={l.phase} style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text)' }}>
                  <span>{l.phase === 'separating' ? `separating (at ${l.avg_pct}% avg)` : l.phase === 'ready' ? 'listening (mixer ready)' : l.phase}</span>
                  <span>{l.n} · after {l.avg_secs}s avg</span>
                </div>
              ))}
            </div>
          ) : <div className="adm-stat-sub">No exits recorded yet</div>}
        </div>
      </div>

      <div className="adm-row3">
        <div className="adm-card adm-panel">
          <div className="adm-panel-h" style={{ marginBottom: 10 }}>The upgrade message</div>
          <div className="adm-stat-sub" style={{ marginBottom: 6 }}>{d.modal.opens} opens by {d.modal.visitors} visitors · avg {d.modal.avg_open_secs == null ? '—' : Math.round(d.modal.avg_open_secs) + 's'} open before dismissing · {d.modal.toggles} plan/billing toggles</div>
          <div className="adm-stat-sub" style={{ margin: '10px 0 6px' }}>Opened from</div>
          <KvList map={d.modal.by_from} total={d.modal.opens} empty="Not opened yet" />
          <div className="adm-stat-sub" style={{ margin: '14px 0 6px' }}>Clicked (events)</div>
          <KvList map={d.modal.plan_clicks} total={d.modal.opens} empty="No clicks yet" />
          <div className="adm-stat-sub" style={{ margin: '14px 0 6px' }}>Dismissed via</div>
          <KvList map={d.modal.closes_by_via} total={d.modal.opens} empty="No dismissals yet" />
        </div>
        <div className="adm-card adm-panel">
          <div className="adm-panel-h" style={{ marginBottom: 10 }}>What they did with the mixer</div>
          <div className="adm-stat-sub" style={{ marginBottom: 6 }}>First touch per track — mute / solo / fader</div>
          <KvList map={d.mixes} total={previews} empty="Nobody touched a channel yet" />
          <div className="adm-stat-sub" style={{ margin: '14px 0 6px' }}>Tools</div>
          <KvList map={d.tools} total={previews} empty="No tool use yet" />
          <div className="adm-stat-sub" style={{ margin: '14px 0 6px' }}>Free doorway</div>
          <div style={{ fontSize: 12, color: 'var(--text)' }}>
            Opened in the Studio: {d.studio.handoffs} · actually arrived: {d.studio.arrived}
          </div>
        </div>
        <div className="adm-card adm-panel">
          <div className="adm-panel-h" style={{ marginBottom: 10 }}>Checkout &amp; problems</div>
          <div style={{ fontSize: 12, color: 'var(--text)', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span>Sent to sign-in before checkout: {d.checkout.logins}</span>
            <span>Checkout sessions minted: {d.checkout.starts}</span>
            <span>Checkout errors: {d.checkout.errors}</span>
          </div>
          <div className="adm-stat-sub" style={{ margin: '14px 0 6px' }}>Errors</div>
          {d.errors.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
              {d.errors.map((e) => (
                <div key={e.stage + e.reason} style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--red)' }}>
                  <span>{e.stage}{e.reason ? ` · ${e.reason}` : ''}</span><span>{e.n}</span>
                </div>
              ))}
            </div>
          ) : <div className="adm-stat-sub">None recorded</div>}
          <div className="adm-stat-sub" style={{ margin: '14px 0 6px' }}>Other</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text)' }}>
            {d.side.map((s) => (
              <div key={s.key} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{s.label}</span><span>{s.visitors} <span style={{ color: 'var(--faint)' }}>· {s.n} ev</span></span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {d.purchases.length > 0 && (
        <div>
          <div className="adm-section-label">Purchases attributed to the splitter <span className="muted">— account was seen on /splitter before its Pro plan was created</span></div>
          <div className="adm-card" style={{ padding: 0, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ color: 'var(--dim)', fontSize: 11, letterSpacing: '.04em' }}>
                <th style={{ ...th, textAlign: 'left' }}>EMAIL</th><th style={{ ...th, textAlign: 'left' }}>PLAN</th><th style={th}>FIRST SEEN</th><th style={th}>PAID</th>
              </tr></thead>
              <tbody>
                {d.purchases.map((p) => (
                  <tr key={p.email} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ ...td, textAlign: 'left' }}>{p.email}</td>
                    <td style={{ ...td, textAlign: 'left' }}>{p.plan}</td>
                    <td style={td}>{fmtWhen(p.first_seen)}</td>
                    <td style={td}>{fmtWhen(p.at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div>
        <div className="adm-section-label">By day <span className="muted">— unique visitors reaching each step</span></div>
        <div className="adm-card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ color: 'var(--dim)', fontSize: 11, letterSpacing: '.04em' }}>
              <th style={{ ...th, textAlign: 'left' }}>DAY</th><th style={th}>VISITS</th><th style={th}>UPLOADS</th><th style={th}>PREVIEWS</th><th style={th}>SAW MESSAGE</th><th style={th}>PLAN CLICKS</th>
            </tr></thead>
            <tbody>
              {d.daily.map((r) => {
                const dim = !r.views;
                return (
                  <tr key={r.d} style={{ borderTop: '1px solid var(--border)', color: dim ? 'var(--faint)' : 'var(--text)' }}>
                    <td style={{ ...td, textAlign: 'left' }}>{r.d}</td>
                    <td style={td}>{r.views}</td><td style={td}>{r.uploads}</td><td style={td}>{r.previews}</td><td style={td}>{r.modal}</td><td style={td}>{r.plan_clicks}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <div className="adm-section-label">Recent visitors <span className="muted">— one row per browser, newest first, every step they touched in order</span></div>
        <div className="adm-card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr style={{ color: 'var(--dim)', fontSize: 11, letterSpacing: '.04em' }}>
              <th style={{ ...th, textAlign: 'left' }}>WHEN</th><th style={{ ...th, textAlign: 'left' }}>WHO</th><th style={{ ...th, textAlign: 'left' }}>FROM</th><th style={{ ...th, textAlign: 'left' }}>JOURNEY</th>
            </tr></thead>
            <tbody>
              {d.sessions.length === 0 && (
                <tr><td colSpan={4} style={{ padding: 14, color: 'var(--dim)' }}>No visitors in this range.</td></tr>
              )}
              {d.sessions.map((s) => (
                <tr key={s.vid + s.first_at} style={{ borderTop: '1px solid var(--border)', color: 'var(--text)', verticalAlign: 'top' }}>
                  <td style={{ ...td, textAlign: 'left', whiteSpace: 'nowrap' }} title={`${s.first_at} → ${s.last_at}`}>{fmtWhen(s.first_at)}</td>
                  <td style={{ ...td, textAlign: 'left', whiteSpace: 'nowrap' }}>
                    <div>{s.email || <span style={{ color: 'var(--dim)' }}>{SESSION_TIER[s.tier || ''] || 'Guest'} · {s.vid}</span>}</div>
                    <div style={{ color: 'var(--faint)', fontSize: 11 }}>{s.device || '?'}{s.purchased ? ' · PAID' : ''}</div>
                  </td>
                  <td style={{ ...td, textAlign: 'left', whiteSpace: 'nowrap' }}>
                    <div>{s.source || '?'}</div>
                    <div style={{ color: 'var(--faint)', fontSize: 11 }}>{s.ref || ''}</div>
                  </td>
                  <td style={{ ...td, textAlign: 'left' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {s.steps.map((st, i) => (
                        <span key={i} style={{
                          padding: '2px 7px', borderRadius: 6, fontSize: 11,
                          background: st.startsWith('modal_open') ? 'var(--accent)' : st.startsWith('plan_click') || st.startsWith('checkout') ? 'var(--green, var(--accent))' : st.startsWith('error') || st.startsWith('cancel') ? 'var(--red)' : 'var(--surface-2)',
                          color: st.startsWith('modal_open') || st.startsWith('plan_click') || st.startsWith('checkout') || st.startsWith('error') || st.startsWith('cancel') ? '#fff' : 'var(--text)',
                        }}>{st}</span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Uninstalls ──────────────────────────────────────────────────────────────
// Exit feedback from riffloop.app/uninstall. The `reason` keys are the chip
// values the page posts (data-r) — keep these in sync with that page. 'none' is
// a row where the user opened the page and left without picking anything.
const REASON_META: Record<string, { label: string; color: string }> = {
  didnt_work:       { label: "Didn't work on my videos", color: 'var(--red)' },
  quality:          { label: 'Separation quality',       color: 'var(--red)' },
  too_expensive:    { label: 'Too expensive',            color: 'var(--amber)' },
  found_alternative:{ label: 'Found another tool',       color: 'var(--amber)' },
  too_complicated:  { label: 'Too complicated',          color: 'var(--amber)' },
  just_testing:     { label: 'Just trying it out',       color: 'var(--dim)' },
  other:            { label: 'Something else',           color: 'var(--dim)' },
  none:             { label: 'No reason given',          color: 'var(--faint)' },
};
function reasonMeta(r: string) {
  return REASON_META[r || 'none'] || { label: r, color: 'var(--dim)' };
}

function UninstallsList({ onOpen, onExpire, mode }: { onOpen: (id: string) => void; onExpire: () => void; mode: 'simple' | 'advanced' }) {
  const simple = mode === 'simple';
  // Trailing 44px = the chevron column (same affordance as the Users table).
  const cols = simple ? 'minmax(180px,1.5fr) 170px minmax(200px,2fr) 110px 44px' : 'minmax(180px,1.4fr) 170px minmax(200px,2fr) 110px 90px 80px 110px 44px';
  const minW = simple ? 564 : 944;
  const [r, setR] = useState<AdminUninstallsResp | null>(null);
  const [qLive, setQLive] = useState('');
  const [q, setQ] = useState('');
  const [reason, setReason] = useState('all');
  const [notesOnly, setNotesOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [openNote, setOpenNote] = useState<number | null>(null);
  const per = 25;

  useEffect(() => { const t = setTimeout(() => { setQ(qLive); setPage(1); }, 300); return () => clearTimeout(t); }, [qLive]);
  useEffect(() => {
    setLoading(true);
    setOpenNote(null); // the query changed — an expanded row id from the old page is meaningless
    adminApi.uninstalls({ q, reason, notes: notesOnly, page, per })
      .then(setR)
      .catch((e) => { if (isExpired(e)) onExpire(); })
      .finally(() => setLoading(false));
  }, [q, reason, notesOnly, page, onExpire]);

  // Open the user — unless the click was really the end of a text selection. A drag
  // that starts in the note and ends outside it dispatches `click` on the ROW (the
  // common ancestor of the two targets), so the note cell's stopPropagation never
  // runs; without this guard, copying a verbatim quote would yank the drawer open.
  const openUser = (userId: string) => {
    const sel = typeof window !== 'undefined' ? window.getSelection() : null;
    if (sel && sel.toString().trim()) return;
    onOpen(userId);
  };

  const rows = r?.rows ?? [];
  const total = r?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / per));
  const gravatars = useGravatars(rows.map((x) => x.email || '').filter(Boolean));

  async function exportAll() {
    try {
      const all = await adminApi.uninstalls({ q, reason, notes: notesOnly, page: 1, per: 100 });
      const head = ['When', 'Name', 'Email', 'Reason', 'Note', 'Plan at uninstall', 'Plan now', 'Separations', 'Ext version', 'User ID'];
      const body = all.rows.map((x) => [
        x.created_at || '', x.name || '', x.email || '', reasonMeta(x.reason).label, x.note || '',
        x.reported_plan, x.plan ? planText(x.plan) : '', x.separations, x.ext_version || '', x.user_id || '',
      ]);
      downloadCsv('riffloop-uninstalls.csv', [head, ...body]);
    } catch (e) { if (isExpired(e)) onExpire(); }
  }

  // Reason chips double as the breakdown — count comes from the whole table, not the page.
  const byReason = r?.by_reason ?? [];
  const topCount = byReason.length ? Math.max(...byReason.map((b) => b.c)) : 0;

  const KPIS = [
    { label: 'Total uninstalls', value: (r?.stats.all ?? 0).toLocaleString(), sub: 'all time', color: 'var(--text)' },
    { label: 'Last 30 days', value: (r?.stats.last30 ?? 0).toLocaleString(), sub: 'recent churn', color: 'var(--amber)' },
    { label: 'Left a note', value: (r?.stats.with_notes ?? 0).toLocaleString(), sub: 'verbatim feedback', color: 'var(--green)' },
    { label: 'Identified', value: (r?.stats.identified ?? 0).toLocaleString(), sub: 'tied to an account', color: 'var(--accent)' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="adm-statgrid">
        {KPIS.map((k) => (
          <div className="adm-card adm-stat" key={k.label}>
            <div className="adm-stat-k">{k.label}</div>
            <div className="adm-stat-v" style={{ color: k.color }}>{k.value}</div>
            <div className="adm-stat-sub">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Why they left — the breakdown that actually drives the roadmap. */}
      {!simple && byReason.length > 0 && (
        <div className="adm-card" style={{ padding: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--dim)', marginBottom: 14 }}>WHY THEY LEFT</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {byReason.map((b) => {
              const m = reasonMeta(b.reason);
              const pct = topCount ? Math.round((b.c / topCount) * 100) : 0;
              return (
                <div key={b.reason} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 150, fontSize: 13, color: 'var(--dim)', flexShrink: 0 }} className="adm-ellip">{m.label}</div>
                  <div style={{ flex: 1, height: 8, borderRadius: 99, background: 'var(--surface-3)', overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', borderRadius: 99, background: m.color, transition: 'width .3s' }} />
                  </div>
                  <div style={{ width: 38, textAlign: 'right', fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{b.c}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--faint)' }}><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>
          <input className="adm-input" style={{ paddingLeft: 42 }} placeholder="Search notes, name or email…" value={qLive} onChange={(e) => setQLive(e.target.value)} />
        </div>
        <span style={{ fontSize: 14, color: 'var(--dim)', fontWeight: 600, whiteSpace: 'nowrap' }}>{total} result{total === 1 ? '' : 's'}</span>
        <button className="adm-btn" onClick={exportAll}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12M7 10l5 5 5-5M5 21h14" /></svg>Export
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className={`adm-chip ${reason === 'all' ? 'on' : ''}`} onClick={() => { setReason('all'); setPage(1); }}>All reasons</button>
        {byReason.map((b) => (
          <button key={b.reason} className={`adm-chip ${reason === b.reason ? 'on' : ''}`} onClick={() => { setReason(b.reason); setPage(1); }}>
            {reasonMeta(b.reason).label} <span style={{ opacity: 0.6 }}>{b.c}</span>
          </button>
        ))}
        <button className={`adm-chip ${notesOnly ? 'on' : ''}`} onClick={() => { setNotesOnly((v) => !v); setPage(1); }}>With a note</button>
      </div>

      {/* Desktop table */}
      <div className="adm-table">
        <div className="adm-table-min" style={{ minWidth: minW }}>
          <div className="adm-tr adm-th" style={{ gridTemplateColumns: cols }}>
            <div>User</div>
            <div>Reason</div>
            <div>What they said</div>
            {!simple && <><div>Plan then</div><div>Used</div><div>Version</div></>}
            <div>When</div>
            <div />
          </div>
          {rows.map((x) => {
            const m = reasonMeta(x.reason);
            // Capture the id: `known` is a boolean and won't narrow user_id inside the closure.
            const uid = x.user_id;
            const known = !!uid;
            const expanded = openNote === x.id;
            return (
              // Click anywhere on an identified row → full user detail. Anonymous rows
              // have no account to open, so they get no .adm-trow (no hover/pointer lie)
              // and no handler; their note stays readable via the note cell below.
              <div
                className={`adm-tr${known ? ' adm-trow' : ''}`}
                key={x.id}
                style={{ gridTemplateColumns: cols, alignItems: 'flex-start' }}
                onClick={uid ? () => openUser(uid) : undefined}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
                  {known ? (
                    <Avatar name={x.name} email={x.email || ''} picture={x.picture_url} gravatar={gravatars[x.email || '']} size={34} />
                  ) : (
                    <span style={{ width: 34, height: 34, borderRadius: 99, background: 'var(--surface-3)', color: 'var(--faint)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" /></svg>
                    </span>
                  )}
                  <div style={{ minWidth: 0 }}>
                    <div className="adm-ellip" style={{ fontSize: 14, fontWeight: 600, color: known ? 'var(--text)' : 'var(--faint)' }}>
                      {known ? (x.name || x.email) : 'Anonymous'}
                    </div>
                    <div className="adm-ellip" style={{ fontSize: 12, color: 'var(--faint)' }}>
                      {known ? x.email : (x.cid ? 'install ' + x.cid.slice(0, 8) : 'signed out')}
                    </div>
                  </div>
                </div>
                <div><span className="adm-badge" style={{ color: m.color, background: 'var(--surface-3)' }}>{m.label}</span></div>
                {/* The note owns its own click: stopPropagation keeps a long-note
                    expand from also opening the drawer. Load-bearing — without it
                    identified+note rows would do both at once. */}
                <div
                  onClick={x.note ? (e) => { e.stopPropagation(); setOpenNote(expanded ? null : x.id); } : undefined}
                  title={x.note ? (expanded ? 'Click to collapse' : 'Click to read in full') : undefined}
                  style={{ fontSize: 13, color: x.note ? 'var(--text)' : 'var(--faint)', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', cursor: x.note ? 'pointer' : undefined, ...(expanded ? {} : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }) }}
                >
                  {x.note || '—'}
                </div>
                {!simple && <>
                  <div style={{ fontSize: 13, color: 'var(--dim)' }}>{x.reported_plan}</div>
                  <div style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', color: 'var(--dim)' }}>{known ? x.separations.toLocaleString() : '—'}</div>
                  <div style={{ fontSize: 13, color: 'var(--faint)' }}>{x.ext_version || '—'}</div>
                </>}
                <div style={{ fontSize: 13, color: 'var(--dim)' }} title={fmtDateTime(x.created_at)}>{rel(x.created_at)}</div>
                {/* Cell always renders to fill the grid column; the glyph only when there's a user to open. */}
                <div style={{ color: 'var(--faint)', display: 'flex', justifyContent: 'flex-end' }}>
                  {known && <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>}
                </div>
              </div>
            );
          })}
          {!loading && rows.length === 0 && <div style={{ padding: 46, textAlign: 'center', color: 'var(--faint)', fontSize: 14 }}>No uninstall feedback yet.</div>}
        </div>
      </div>

      {/* Mobile cards */}
      <div className="adm-ucards">
        {rows.map((x) => {
          const m = reasonMeta(x.reason);
          const uid = x.user_id;
          const known = !!uid;
          return (
            // .adm-ucard is unconditionally cursor:pointer (shared with the Users
            // list), so anonymous cards need the inline override to stop claiming
            // they open something.
            <div className="adm-ucard" key={x.id} style={{ cursor: uid ? 'pointer' : 'default' }} onClick={uid ? () => openUser(uid) : undefined}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                {known ? (
                  <Avatar name={x.name} email={x.email || ''} picture={x.picture_url} gravatar={gravatars[x.email || '']} size={40} />
                ) : (
                  <span style={{ width: 40, height: 40, borderRadius: 99, background: 'var(--surface-3)', color: 'var(--faint)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" /></svg>
                  </span>
                )}
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="adm-ellip" style={{ fontSize: 15, fontWeight: 600, color: known ? 'var(--text)' : 'var(--faint)' }}>{known ? (x.name || x.email) : 'Anonymous'}</div>
                  <div className="adm-ellip" style={{ fontSize: 12, color: 'var(--faint)' }}>{known ? x.email : 'signed out'}</div>
                </div>
                <span className="adm-badge" style={{ color: m.color, background: 'var(--surface-3)', flexShrink: 0 }}>{m.label}</span>
              </div>
              {x.note && <div style={{ marginTop: 11, fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{x.note}</div>}
              <div style={{ display: 'flex', gap: 18, marginTop: 13, paddingTop: 13, borderTop: '1px solid var(--border)', fontSize: 12 }}>
                <div><span style={{ color: 'var(--faint)' }}>Plan</span> <span style={{ fontWeight: 700 }}>{x.reported_plan}</span></div>
                {known && <div><span style={{ color: 'var(--faint)' }}>Sep</span> <span style={{ fontWeight: 700 }}>{x.separations}</span></div>}
                <div style={{ flex: 1, textAlign: 'right', color: 'var(--faint)' }}>{rel(x.created_at)}</div>
              </div>
            </div>
          );
        })}
        {!loading && rows.length === 0 && <div className="adm-empty">No uninstall feedback yet.</div>}
      </div>

      <div className="adm-pager">
        <span style={{ fontSize: 13, color: 'var(--faint)' }}>Page {Math.min(page, pages)} of {pages}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      </div>
    </div>
  );
}

// ── Reviews (in-extension star prompt) ──────────────────────────────────────
// The extension shows a 5-star modal after a user's 2nd separation: 4-5 stars
// get routed on to the Chrome Web Store, 1-3 stars leave a note here instead.
// Every pick lands as a row, so this page shows the FULL distribution — the
// store only ever sees the happy tail.
const STAR_COLOR: Record<number, string> = {
  5: 'var(--green)', 4: 'var(--accent)', 3: 'var(--amber)', 2: 'var(--red)', 1: 'var(--red)',
};
function Stars({ n, size = 15 }: { n: number; size?: number }) {
  return (
    <span style={{ display: 'inline-flex', gap: 1, color: STAR_COLOR[n] || 'var(--dim)', fontSize: size, lineHeight: 1 }} title={`${n} of 5`}>
      {[1, 2, 3, 4, 5].map((i) => <span key={i} style={{ opacity: i <= n ? 1 : 0.22 }}>★</span>)}
    </span>
  );
}

function ReviewsList({ onOpen, onExpire, mode }: { onOpen: (id: string) => void; onExpire: () => void; mode: 'simple' | 'advanced' }) {
  const simple = mode === 'simple';
  // Trailing 44px = the chevron column; advanced adds plan/used/version and a
  // 40px delete column (rows are public-writable, so spam needs a broom).
  const cols = simple ? 'minmax(180px,1.5fr) 120px minmax(200px,2fr) 110px 44px' : 'minmax(180px,1.4fr) 120px minmax(200px,2fr) 110px 90px 80px 110px 40px 44px';
  const minW = simple ? 564 : 980;
  const [r, setR] = useState<AdminReviewsResp | null>(null);
  const [qLive, setQLive] = useState('');
  const [q, setQ] = useState('');
  const [stars, setStars] = useState('all');
  const [notesOnly, setNotesOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [bump, setBump] = useState(0);   // re-fetch after a delete
  const [loading, setLoading] = useState(true);
  const [openNote, setOpenNote] = useState<number | null>(null);
  const per = 25;

  useEffect(() => { const t = setTimeout(() => { setQ(qLive); setPage(1); }, 300); return () => clearTimeout(t); }, [qLive]);
  useEffect(() => {
    setLoading(true);
    setOpenNote(null); // the query changed — an expanded row id from the old page is meaningless
    adminApi.reviews({ q, stars, notes: notesOnly, page, per })
      .then(setR)
      .catch((e) => { if (isExpired(e)) onExpire(); })
      .finally(() => setLoading(false));
  }, [q, stars, notesOnly, page, bump, onExpire]);

  // Same text-selection guard as the Uninstalls table: a drag-select ending
  // outside the note cell dispatches click on the ROW.
  const openUser = (userId: string) => {
    const sel = typeof window !== 'undefined' ? window.getSelection() : null;
    if (sel && sel.toString().trim()) return;
    onOpen(userId);
  };

  async function removeRow(id: number) {
    if (!window.confirm('Delete this review row? Removes it from the admin only — nothing user-facing.')) return;
    try { await adminApi.deleteReview(id); setBump((b) => b + 1); }
    catch (e) { if (isExpired(e)) onExpire(); }
  }

  const rows = r?.rows ?? [];
  const total = r?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / per));
  const gravatars = useGravatars(rows.map((x) => x.email || '').filter(Boolean));

  async function exportAll() {
    try {
      const all = await adminApi.reviews({ q, stars, notes: notesOnly, page: 1, per: 100 });
      const head = ['When', 'Name', 'Email', 'Stars', 'Feedback', 'Plan at rating', 'Plan now', 'Separations', 'Source', 'Ext version', 'User ID'];
      const body = all.rows.map((x) => [
        x.created_at || '', x.name || '', x.email || '', x.stars, x.note || '',
        x.reported_plan, x.plan ? planText(x.plan) : '', x.separations, x.source || '', x.ext_version || '', x.user_id || '',
      ]);
      downloadCsv('riffloop-reviews.csv', [head, ...body]);
    } catch (e) { if (isExpired(e)) onExpire(); }
  }

  const byStars = r?.by_stars ?? [];
  const topCount = byStars.length ? Math.max(...byStars.map((b) => b.c)) : 0;
  // Always render all five bars, 5★ first, so gaps read as zeros not omissions.
  const starRows = [5, 4, 3, 2, 1].map((n) => ({ stars: n, c: byStars.find((b) => b.stars === n)?.c ?? 0 }));

  const KPIS = [
    { label: 'Average rating', value: (r?.stats.avg ?? 0).toFixed(2), sub: 'out of 5', color: 'var(--amber)' },
    { label: 'Total ratings', value: (r?.stats.all ?? 0).toLocaleString(), sub: 'all time', color: 'var(--text)' },
    { label: 'Sent to the store', value: (r?.stats.promoters ?? 0).toLocaleString(), sub: '4-5 stars → CWS', color: 'var(--green)' },
    { label: 'Left feedback', value: (r?.stats.with_notes ?? 0).toLocaleString(), sub: 'verbatim notes', color: 'var(--accent)' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="adm-statgrid">
        {KPIS.map((k) => (
          <div className="adm-card adm-stat" key={k.label}>
            <div className="adm-stat-k">{k.label}</div>
            <div className="adm-stat-v" style={{ color: k.color }}>{k.value}</div>
            <div className="adm-stat-sub">{k.sub}</div>
          </div>
        ))}
      </div>

      {!simple && (r?.stats.all ?? 0) > 0 && (
        <div className="adm-card" style={{ padding: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--dim)', marginBottom: 14 }}>RATING SPREAD</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {starRows.map((b) => {
              const pct = topCount ? Math.round((b.c / topCount) * 100) : 0;
              return (
                <div key={b.stars} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 150, flexShrink: 0 }}><Stars n={b.stars} size={13} /></div>
                  <div style={{ flex: 1, height: 8, borderRadius: 99, background: 'var(--surface-3)', overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', borderRadius: 99, background: STAR_COLOR[b.stars], transition: 'width .3s' }} />
                  </div>
                  <div style={{ width: 38, textAlign: 'right', fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{b.c}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--faint)' }}><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>
          <input className="adm-input" style={{ paddingLeft: 42 }} placeholder="Search feedback, name or email…" value={qLive} onChange={(e) => setQLive(e.target.value)} />
        </div>
        <span style={{ fontSize: 14, color: 'var(--dim)', fontWeight: 600, whiteSpace: 'nowrap' }}>{total} result{total === 1 ? '' : 's'}</span>
        <button className="adm-btn" onClick={exportAll}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12M7 10l5 5 5-5M5 21h14" /></svg>Export
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className={`adm-chip ${stars === 'all' ? 'on' : ''}`} onClick={() => { setStars('all'); setPage(1); }}>All ratings</button>
        {starRows.filter((b) => b.c > 0).map((b) => (
          <button key={b.stars} className={`adm-chip ${stars === String(b.stars) ? 'on' : ''}`} onClick={() => { setStars(String(b.stars)); setPage(1); }}>
            {b.stars}★ <span style={{ opacity: 0.6 }}>{b.c}</span>
          </button>
        ))}
        <button className={`adm-chip ${notesOnly ? 'on' : ''}`} onClick={() => { setNotesOnly((v) => !v); setPage(1); }}>With feedback</button>
      </div>

      {/* Desktop table */}
      <div className="adm-table">
        <div className="adm-table-min" style={{ minWidth: minW }}>
          <div className="adm-tr adm-th" style={{ gridTemplateColumns: cols }}>
            <div>User</div>
            <div>Rating</div>
            <div>What they said</div>
            {!simple && <><div>Plan then</div><div>Used</div><div>Version</div></>}
            <div>When</div>
            {!simple && <div />}
            <div />
          </div>
          {rows.map((x) => {
            const uid = x.user_id;
            const known = !!uid;
            const expanded = openNote === x.id;
            return (
              <div
                className={`adm-tr${known ? ' adm-trow' : ''}`}
                key={x.id}
                style={{ gridTemplateColumns: cols, alignItems: 'flex-start' }}
                onClick={uid ? () => openUser(uid) : undefined}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
                  {known ? (
                    <Avatar name={x.name} email={x.email || ''} picture={x.picture_url} gravatar={gravatars[x.email || '']} size={34} />
                  ) : (
                    <span style={{ width: 34, height: 34, borderRadius: 99, background: 'var(--surface-3)', color: 'var(--faint)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" /></svg>
                    </span>
                  )}
                  <div style={{ minWidth: 0 }}>
                    <div className="adm-ellip" style={{ fontSize: 14, fontWeight: 600, color: known ? 'var(--text)' : 'var(--faint)' }}>
                      {known ? (x.name || x.email) : 'Anonymous'}
                    </div>
                    <div className="adm-ellip" style={{ fontSize: 12, color: 'var(--faint)' }}>
                      {known ? x.email : 'signed out'}
                    </div>
                  </div>
                </div>
                <div style={{ paddingTop: 8 }}><Stars n={x.stars} /></div>
                {/* Note cell owns its click (expand), same as the Uninstalls table. */}
                <div
                  onClick={x.note ? (e) => { e.stopPropagation(); setOpenNote(expanded ? null : x.id); } : undefined}
                  title={x.note ? (expanded ? 'Click to collapse' : 'Click to read in full') : undefined}
                  style={{ fontSize: 13, color: x.note ? 'var(--text)' : 'var(--faint)', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', cursor: x.note ? 'pointer' : undefined, ...(expanded ? {} : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }) }}
                >
                  {x.note || (x.stars >= 4 ? '→ sent to the store' : '—')}
                </div>
                {!simple && <>
                  <div style={{ fontSize: 13, color: 'var(--dim)' }}>{x.reported_plan}</div>
                  <div style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', color: 'var(--dim)' }}>{known ? x.separations.toLocaleString() : '—'}</div>
                  <div style={{ fontSize: 13, color: 'var(--faint)' }}>{x.ext_version || '—'}</div>
                </>}
                <div style={{ fontSize: 13, color: 'var(--dim)' }} title={fmtDateTime(x.created_at)}>{rel(x.created_at)}</div>
                {!simple && (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeRow(x.id); }}
                      title="Delete row"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--faint)', padding: 4, borderRadius: 6 }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6" /></svg>
                    </button>
                  </div>
                )}
                <div style={{ color: 'var(--faint)', display: 'flex', justifyContent: 'flex-end' }}>
                  {known && <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>}
                </div>
              </div>
            );
          })}
          {!loading && rows.length === 0 && <div style={{ padding: 46, textAlign: 'center', color: 'var(--faint)', fontSize: 14 }}>No ratings yet — the prompt shows after a user's 2nd separation.</div>}
        </div>
      </div>

      {/* Mobile cards */}
      <div className="adm-ucards">
        {rows.map((x) => {
          const uid = x.user_id;
          const known = !!uid;
          return (
            <div className="adm-ucard" key={x.id} style={{ cursor: uid ? 'pointer' : 'default' }} onClick={uid ? () => openUser(uid) : undefined}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                {known ? (
                  <Avatar name={x.name} email={x.email || ''} picture={x.picture_url} gravatar={gravatars[x.email || '']} size={40} />
                ) : (
                  <span style={{ width: 40, height: 40, borderRadius: 99, background: 'var(--surface-3)', color: 'var(--faint)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" /></svg>
                  </span>
                )}
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="adm-ellip" style={{ fontSize: 15, fontWeight: 600, color: known ? 'var(--text)' : 'var(--faint)' }}>{known ? (x.name || x.email) : 'Anonymous'}</div>
                  <div className="adm-ellip" style={{ fontSize: 12, color: 'var(--faint)' }}>{known ? x.email : 'signed out'}</div>
                </div>
                <Stars n={x.stars} />
              </div>
              {x.note && <div style={{ marginTop: 11, fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{x.note}</div>}
              <div style={{ display: 'flex', gap: 18, marginTop: 13, paddingTop: 13, borderTop: '1px solid var(--border)', fontSize: 12 }}>
                <div><span style={{ color: 'var(--faint)' }}>Plan</span> <span style={{ fontWeight: 700 }}>{x.reported_plan}</span></div>
                {known && <div><span style={{ color: 'var(--faint)' }}>Sep</span> <span style={{ fontWeight: 700 }}>{x.separations}</span></div>}
                <div style={{ flex: 1, textAlign: 'right', color: 'var(--faint)' }}>{rel(x.created_at)}</div>
              </div>
            </div>
          );
        })}
        {!loading && rows.length === 0 && <div className="adm-empty">No ratings yet.</div>}
      </div>

      <div className="adm-pager">
        <span style={{ fontSize: 13, color: 'var(--faint)' }}>Page {Math.min(page, pages)} of {pages}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      </div>
    </div>
  );
}

// ── Detail drawer ───────────────────────────────────────────────────────────
const PLAN_SEG: { label: string; target: AdminPlanTarget; match: PlanLabel }[] = [
  { label: 'Free', target: 'free', match: 'free' },
  { label: 'Pro', target: 'monthly', match: 'pro' },
  { label: 'Lifetime', target: 'lifetime', match: 'lifetime' },
];

function UserDrawer({ id, onClose, onExpire, onChanged }: { id: string; onClose: () => void; onExpire: () => void; onChanged: () => void }) {
  const [d, setD] = useState<AdminUserDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const load = useCallback(() => {
    adminApi.user(id).then(setD).catch((e) => { if (isExpired(e)) onExpire(); else setErr('Could not load user.'); });
  }, [id, onExpire]);
  useEffect(() => { load(); }, [load]);

  const email = d?.user.email || '';
  const gravatars = useGravatars(email ? [email] : []);

  async function setPlan(target: AdminPlanTarget) {
    if (saving) return;
    setSaving(true);
    try { await adminApi.setPlan(id, target); load(); onChanged(); }
    catch (e) { if (isExpired(e)) onExpire(); }
    finally { setSaving(false); }
  }
  async function addNote() {
    if (!noteText.trim() || savingNote) return;
    setSavingNote(true);
    try { await adminApi.addNote(id, noteText.trim()); setNoteText(''); load(); }
    catch (e) { if (isExpired(e)) onExpire(); }
    finally { setSavingNote(false); }
  }
  function exportOne() {
    if (!d) return;
    const u = d.user, sub = d.subscription, a = d.activity;
    downloadCsv(`riffloop-user-${u.id}.csv`, [
      ['Name', 'Email', 'Plan', 'Status', 'Separations', 'Sections', 'Distinct tracks', 'Joined', 'Last seen', 'User ID'],
      [u.name || '', u.email, planText(sub.plan), sub.status, a.separations.total, a.sections.count, a.separations.distinct_tracks, u.created_at || '', u.last_login_at || '', u.id],
    ]);
  }

  const pr = presence(d?.user.last_login_at);
  const sub = d?.subscription;
  const product = sub?.product_id === '134701' ? 'Monthly' : sub?.product_id === '134681' ? 'Yearly' : sub?.product_id === '134702' ? 'Lifetime' : (sub?.product_id || '—');

  // Billing — transaction id + amount/currency now come from the PayPro IPN
  // (persisted on the plans row). Amount falls back to the list price for $0 test
  // orders. Next-renewal isn't stored yet, so it reads "—".
  const dash = '—';
  const isPaid = !!sub && sub.plan !== 'free';
  const paidAmt = sub?.amount != null && Number(sub.amount) > 0 ? '$' + Number(sub.amount).toFixed(2) : null;
  const bill = {
    txn: sub?.order_id || dash,
    amount: !isPaid ? dash : (paidAmt ?? (sub!.plan === 'lifetime' ? '$99.00' : sub!.product_id === '134681' ? '$39.00' : '$5.95')),
    currency: isPaid ? (sub!.currency || 'USD') : dash,
    lastCharged: isPaid ? fmtDate(sub!.updated_at) : dash,
    nextRenewal: dash,
  };

  return (
    <div className="adm-drawer-wrap">
      <div className="adm-overlay" onClick={onClose} />
      <div className="adm-drawer">
        <div className="adm-drawer-head">
          <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.07em', color: 'var(--dim)', textTransform: 'uppercase' }}>User detail</span>
          <button className="adm-iconbtn" style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--surface-2)' }} onClick={onClose} aria-label="Close"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg></button>
        </div>

        {err && <div style={{ padding: 20 }}><div className="adm-loginerr">{err}</div></div>}
        {!d && !err && <div className="adm-loading" style={{ minHeight: 200 }}><div className="adm-spin" /></div>}

        {d && (
          <div style={{ padding: '22px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <Avatar name={d.user.name} email={d.user.email} picture={d.user.picture_url} gravatar={gravatars[d.user.email]} size={58} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 19, fontWeight: 800, letterSpacing: '-.01em' }}>{d.user.name || d.user.email}</div>
                <div className="adm-ellip" style={{ fontSize: 13, color: 'var(--dim)' }}>{d.user.email}</div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <span className={`adm-badge ${d.subscription.plan}`} style={{ padding: '5px 11px' }}>{planText(d.subscription.plan)}</span>
              <span className="adm-badge" style={{ color: pr.color, background: 'var(--surface-3)', display: 'flex', alignItems: 'center', gap: 6, padding: '5px 11px' }}><span style={{ width: 7, height: 7, borderRadius: 99, background: pr.color }} />{pr.label}</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 22 }}>
              {[
                { k: 'Separations', v: d.activity.separations.total.toLocaleString() },
                { k: 'Sections', v: String(d.activity.sections.count) },
                { k: 'Distinct tracks', v: String(d.activity.separations.distinct_tracks) },
                { k: 'Last 30d sep', v: String(d.activity.separations.last30) },
              ].map((m) => (
                <div className="adm-ministat" key={m.k}>
                  <div style={{ fontSize: 11, color: 'var(--faint)', fontWeight: 600 }}>{m.k}</div>
                  <div style={{ fontSize: 20, fontWeight: 800, marginTop: 3, fontVariantNumeric: 'tabular-nums' }}>{m.v}</div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 18 }}>
              <div className="adm-kv"><span className="k">Joined</span><span className="v">{fmtDate(d.user.created_at)}</span></div>
              <div className="adm-kv"><span className="k">Last seen</span><span className="v">{fmtDateTime(d.user.last_login_at)}</span></div>
              {/* Acquisition — first-touch, captured once at signup. Rows created
                  before 2026-08-28 read "Unknown" rather than being hidden, so the
                  absence of data is visible instead of looking like "Direct". */}
              <div className="adm-kv"><span className="k">Came from</span>
                <span className="v" style={{ display: 'flex', alignItems: 'center', gap: 7, color: sourceTone(d.user.signup_source) }}>
                  {sourceText(d.user.signup_source)}
                  {d.user.signup_surface && d.user.signup_surface !== 'unknown' && (
                    <span style={{ fontSize: 11, color: 'var(--faint)' }}>via {d.user.signup_surface}</span>
                  )}
                </span>
              </div>
              {d.user.signup_landing && (
                <div className="adm-kv"><span className="k">Landed on</span><span className="v" style={{ color: 'var(--faint)', fontSize: 12 }}>{d.user.signup_landing}</span></div>
              )}
              {d.user.signup_referrer && (
                <div className="adm-kv"><span className="k">Referrer</span><span className="v" style={{ color: 'var(--faint)', fontSize: 12 }}>{d.user.signup_referrer}</span></div>
              )}
              <div className="adm-kv"><span className="k">Device</span>
                <span className="v" style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <DeviceIcon d={d.user.signup_device} size={14} />
                  {DEVICE_LABEL[d.user.signup_device || 'unknown'] || 'Unknown'}
                  {d.user.last_device && d.user.last_device !== 'unknown' && d.user.last_device !== d.user.signup_device && (
                    <span style={{ fontSize: 11, color: 'var(--faint)' }}>· now {DEVICE_LABEL[d.user.last_device] || d.user.last_device}</span>
                  )}
                </span>
              </div>
              <div className="adm-kv"><span className="k">Status</span><span className="v">{d.subscription.status}</span></div>
              <div className="adm-kv"><span className="k">Product</span><span className="v">{product}</span></div>
              <div className="adm-kv"><span className="k">Subscription ID</span><span className="v" style={{ color: 'var(--faint)', fontSize: 12 }}>{d.subscription.subscription_id || '—'}</span></div>
              <div className="adm-kv"><span className="k">User ID</span><span className="v" style={{ color: 'var(--faint)', fontSize: 12 }}>{d.user.id}</span></div>
            </div>

            {/* Billing */}
            <div style={{ marginTop: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span className="adm-panel-h">Billing</span>
                <span style={{ height: 1, flex: 1, background: 'var(--border)' }} />
              </div>
              <div className="adm-kv"><span className="k">Transaction ID</span><span className="v" style={{ color: 'var(--faint)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 13, wordBreak: 'break-all' }}>{bill.txn}</span></div>
              <div className="adm-kv"><span className="k">Amount</span><span className="v" style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{bill.amount}</span></div>
              <div className="adm-kv"><span className="k">Currency</span><span className="v">{bill.currency}</span></div>
              <div className="adm-kv"><span className="k">Last charged</span><span className="v">{bill.lastCharged}</span></div>
              <div className="adm-kv"><span className="k">Next renewal</span><span className="v" style={{ color: bill.nextRenewal === dash ? 'var(--faint)' : 'var(--text)' }}>{bill.nextRenewal}</span></div>
            </div>

            <div style={{ marginTop: 22 }}>
              <div className="adm-panel-h" style={{ marginBottom: 9 }}>Change plan</div>
              <div className="adm-seg">
                {PLAN_SEG.map((p) => (
                  <button key={p.label} className={d.subscription.plan === p.match ? 'on' : ''} disabled={saving} onClick={() => setPlan(p.target)}>{p.label}</button>
                ))}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--faint)', marginTop: 8 }}>Manual override of the canonical plan. A later PayPro event for this email may change it.</div>
            </div>

            {d.activity.separations.by_stem.length > 0 && (
              <div style={{ marginTop: 22 }}>
                <div className="adm-panel-h" style={{ marginBottom: 9 }}>Most-used stems</div>
                <div className="adm-stems">
                  {d.activity.separations.by_stem.map((s) => (
                    <span className="adm-stem" key={s.stem}><span className="dot" style={{ background: STEM_COLOR[s.stem as keyof typeof STEM_COLOR] || 'var(--dim)' }} />{stemLabel(s.stem)} · {s.c}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Where they work + the pre-upgrade friction signal */}
            <div style={{ marginTop: 22 }}>
              <div className="adm-panel-h" style={{ marginBottom: 9 }}>Usage</div>
              <div className="adm-kv"><span className="k">Extension vs Studio</span><span className="v">{(() => {
                const surf = d.activity.separations.by_surface || [];
                const ext = surf.find((x) => x.surface === 'extension')?.c || 0;
                const stu = surf.find((x) => x.surface === 'studio')?.c || 0;
                const andr = surf.find((x) => x.surface === 'android')?.c || 0;
                const total = d.activity.separations.total || 0;
                // Derived here rather than sent by the detail route — same
                // precedence as the users list, so the drawer can't disagree
                // with the row you clicked to open it.
                const k = andr ? 'android' : ext && stu ? 'both' : ext ? 'extension' : stu ? 'studio' : total ? 'unknown' : 'none';
                return (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <UsesTag u={{ uses: k, separations: total, sep_ext: ext, sep_studio: stu, sep_android: andr }} />
                    <span style={{ color: 'var(--faint)', fontWeight: 400 }}>{ext} · {stu}{andr ? ` · ${andr} Android` : ''}</span>
                  </span>
                );
              })()}</span></div>
              <div className="adm-kv"><span className="k">First separation</span><span className="v">{fmtDate(d.activity.separations.first)}</span></div>
              <div className="adm-kv"><span className="k">Cap-hits (free wall)</span><span className="v" style={{ fontWeight: 700, color: (d.activity.cap_hits ?? 0) > 0 ? 'var(--accent)' : 'var(--text)' }}>{d.activity.cap_hits ?? 0}</span></div>
            </div>

            {/* Recent separations — full source URL (clickable when a safe YouTube link).
                Guarded with `|| []` so an older backend response (no `recent`) can't crash the drawer. */}
            <div style={{ marginTop: 22 }}>
              <div className="adm-panel-h" style={{ marginBottom: 9 }}>Recent separations</div>
              {(d.activity.separations.recent || []).length === 0 ? <div style={{ fontSize: 13, color: 'var(--faint)' }}>No separations logged yet.</div> : (
                <div className="adm-feed">
                  {(d.activity.separations.recent || []).map((s, i) => {
                    const href = safeYouTubeUrl(s.source_url);
                    // Lead with the track's real name when we have it — "dQw4w9WgXcQ"
                    // doesn't answer "which tracks did they use". Falls back to the URL
                    // (older rows / never-saved-a-section) so nothing goes blank.
                    const label = s.title || s.source_url || s.video_id || '—';
                    return (
                      <div className="adm-feed-row" key={`${s.created_at}-${i}`} style={{ padding: '10px 14px' }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div className="adm-ellip" style={{ fontSize: 13, fontWeight: 600 }} title={label}>
                            {href ? <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>{label}</a> : label}
                          </div>
                          <div className="adm-ellip" style={{ fontSize: 12, color: 'var(--faint)' }}>{surfaceLabel(s.surface)} · {stemLabel(s.stem)}</div>
                        </div>
                        <span style={{ fontSize: 12, color: 'var(--faint)', flexShrink: 0 }}>{rel(s.created_at)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── The trail ────────────────────────────────────────────────────────
                What this account did, in order. The Features tab counts events across
                everybody; this is the only place one person's SEQUENCE is readable, and
                that is the question asked the moment somebody upgrades — what were they
                doing in the minutes before. Optional, so a dashboard newer than the API
                it is talking to simply does not draw the panel. */}
            {d.activity.events && (
              <div style={{ marginTop: 22 }}>
                <div className="adm-panel-h" style={{ marginBottom: 9 }}>
                  What they did
                  <span style={{ fontWeight: 400, color: 'var(--faint)', marginLeft: 8, fontSize: 12 }}>
                    {d.activity.events.count} event{d.activity.events.count === 1 ? '' : 's'}
                    {(d.activity.events.by_surface || []).map((s) => ` · ${surfaceLabel(s.surface)} ${s.c}`).join('')}
                  </span>
                </div>
                {(d.activity.events.recent || []).length === 0 ? (
                  <div style={{ fontSize: 13, color: 'var(--faint)' }}>
                    Nothing recorded yet — each surface reports from its released build.
                  </div>
                ) : (
                  <div className="adm-feed">
                    {(() => {
                      let lastDay = '';
                      return (d.activity.events?.recent || []).map((e, i) => {
                        const at = eventAt(e);
                        const day = dayOf(at);
                        const newDay = day !== lastDay;
                        lastDay = day;
                        const meta = metaLine(e.meta);
                        const tone = eventTone(e.event);
                        return (
                          <Fragment key={`${e.created_at}-${i}`}>
                            {newDay && (
                              <div style={{ padding: '7px 14px', fontSize: 11, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--faint)' }}>{day}</div>
                            )}
                            <div className="adm-feed-row" style={{ padding: '8px 14px' }}>
                              {/* Tabular figures, so a column of times reads as a column. */}
                              <span style={{ fontSize: 12, color: 'var(--faint)', flexShrink: 0, width: 68, fontVariantNumeric: 'tabular-nums' }}>{clockOf(at)}</span>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div className="adm-ellip" style={{ fontSize: 13, fontWeight: 600, color: tone }}>{eventLabel(e.event)}</div>
                                {meta && <div className="adm-ellip" style={{ fontSize: 12, color: 'var(--faint)' }} title={meta}>{meta}</div>}
                              </div>
                              <span style={{ fontSize: 11, color: 'var(--faint)', flexShrink: 0 }}>{surfaceLabel(e.surface)}</span>
                            </div>
                          </Fragment>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: 22 }}>
              <div className="adm-panel-h" style={{ marginBottom: 9 }}>Latest sections</div>
              {d.activity.sections.latest.length === 0 ? <div style={{ fontSize: 13, color: 'var(--faint)' }}>No saved sections.</div> : (
                <div className="adm-feed">
                  {d.activity.sections.latest.map((s) => (
                    <div className="adm-feed-row" key={s.id} style={{ padding: '10px 14px' }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div className="adm-ellip" style={{ fontSize: 13, fontWeight: 600 }} title={s.label || undefined}>{s.label || 'Untitled'}</div>
                        {/* The track this section came from — its real name when known, else the raw id. */}
                        <div className="adm-ellip" style={{ fontSize: 12, color: 'var(--faint)' }} title={s.title || undefined}>{stemLabel(s.stem)} · {s.title || s.video_id}</div>
                      </div>
                      <span style={{ fontSize: 12, color: 'var(--faint)', flexShrink: 0 }}>{rel(s.updated_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ marginTop: 22 }}>
              <div className="adm-panel-h" style={{ marginBottom: 9 }}>Notes</div>
              {d.notes.map((n) => (
                <div className={`adm-note ${n.kind}`} key={n.id}>{n.body}<div className="when">{n.kind === 'plan_change' ? 'plan change · ' : ''}{fmtDateTime(n.created_at)}</div></div>
              ))}
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <textarea className="adm-textarea" placeholder="Add a note (billing data, support context)…" value={noteText} onChange={(e) => setNoteText(e.target.value)} />
                <button className="adm-btn" style={{ height: 'auto', alignSelf: 'stretch' }} disabled={savingNote || !noteText.trim()} onClick={addNote}>{savingNote ? '…' : 'Add'}</button>
              </div>
            </div>

            <button className="adm-btn" style={{ width: '100%', marginTop: 14 }} onClick={exportOne}>Export this user (CSV)</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Settings ──────────────────────────────────────────────────────────────────
// Owner controls, redesigned from the "Admin Settings" Claude Design handoff into
// three sections: checkout mode (test/live), editable display prices, and PayPro
// config status. It only writes LOCAL settings — it never reads, writes, or rotates
// any PayPro key and never touches subscriptions or the Vendor API.
const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

function SettingBanner({ tone, children }: { tone: 'warn' | 'err'; children: ReactNode }) {
  const c = tone === 'err' ? 'var(--red)' : 'var(--amber)';
  return (
    <div style={{
      display: 'flex', gap: 8, marginTop: 14, padding: '11px 13px', borderRadius: 11, fontSize: 13, lineHeight: 1.45,
      color: c, background: `color-mix(in srgb, ${c} 12%, transparent)`, border: `1px solid color-mix(in srgb, ${c} 35%, transparent)`,
    }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4M12 17h.01" /></svg>
      <span>{children}</span>
    </div>
  );
}
function PriceRow({ label, v }: { label: string; v: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 12px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 10 }}>
      <span style={{ color: 'var(--dim)' }}>{label}</span>
      <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{v}</span>
    </div>
  );
}
function Eyebrow({ children, danger }: { children: ReactNode; danger?: boolean }) {
  return <div style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '.12em', color: danger ? 'var(--red)' : 'var(--accent)', marginBottom: 7 }}>{children}</div>;
}
function ModeCard({ active, accent, name, sub, onPick }: { active: boolean; accent: string; name: string; sub: string; onPick: () => void }) {
  return (
    <button onClick={onPick} disabled={active} style={{
      textAlign: 'left', font: 'inherit', cursor: active ? 'default' : 'pointer',
      border: `1.5px solid ${active ? accent : 'var(--border)'}`,
      background: active ? `color-mix(in srgb, ${accent} 10%, transparent)` : 'var(--surface-2)',
      borderRadius: 14, padding: 18,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 17, fontWeight: 700, color: active ? accent : 'var(--text)' }}>
          <span style={{ width: 9, height: 9, borderRadius: 99, background: accent }} />{name}
        </span>
        {active && <span style={{ fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '.07em', color: accent }}>ACTIVE</span>}
      </div>
      <div style={{ fontSize: 14, color: 'var(--faint)', fontFamily: MONO }}>{sub}</div>
    </button>
  );
}
function KeyStatus({ name, ok }: { name: string; ok: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: 15, color: 'var(--text)', fontFamily: MONO }}>{name}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: ok ? 'var(--green)' : 'var(--red)', fontWeight: 700, fontSize: 13, fontFamily: MONO }}>
        <span style={{ width: 7, height: 7, borderRadius: 99, background: 'currentColor', boxShadow: ok ? '0 0 8px currentColor' : 'none' }} />
        {ok ? 'CONFIGURED' : 'NOT SET'}
      </span>
    </div>
  );
}
function PriceField({ label, unit, value, onChange }: { label: string; unit: string; value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 14, padding: 16, background: 'var(--surface-2)' }}>
      <div style={{ fontFamily: MONO, fontSize: 11, fontWeight: 500, letterSpacing: '.07em', color: 'var(--faint)', marginBottom: 11 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <span style={{ fontSize: 19, fontWeight: 700, color: 'var(--accent)', fontFamily: MONO }}>$</span>
        <input className="adm-input" inputMode="decimal" value={value}
          onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ''))}
          style={{ fontFamily: MONO, width: '100%', fontSize: 19, fontWeight: 700, padding: '8px 10px' }} />
      </div>
      <div style={{ marginTop: 8, fontSize: 12, color: 'var(--faint)', fontFamily: MONO }}>{unit}</div>
    </div>
  );
}

function SettingsTab({ onExpire }: { onExpire: () => void }) {
  const [s, setS] = useState<AdminSettings | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState<CheckoutMode | null>(null); // mode being switched TO
  const [typed, setTyped] = useState('');
  // Pricing (editable draft, persisted separately from the checkout-mode switch).
  const [prices, setPrices] = useState<PriceSet>({ monthly: '', annual: '', lifetime: '' });
  const [priceDirty, setPriceDirty] = useState(false);
  const [savingPrices, setSavingPrices] = useState(false);
  const [priceErr, setPriceErr] = useState<string | null>(null);
  const [priceSaved, setPriceSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const pricesInit = useRef(false);

  const load = useCallback(() => {
    setErr(null);
    adminApi.settings().then((data) => {
      setS(data);
      // Seed the price draft once; don't clobber an in-progress edit on later reloads
      // (e.g. after a checkout-mode switch refetches settings).
      if (!pricesInit.current) { setPrices(data.prices); pricesInit.current = true; }
    }).catch((e) => { if (isExpired(e)) onExpire(); else setErr('Could not load settings.'); });
  }, [onExpire]);
  useEffect(() => { load(); }, [load]);

  async function apply(mode: CheckoutMode) {
    if (saving) return;
    setSaving(true); setErr(null);
    try { await adminApi.setCheckoutMode(mode); setConfirm(null); setTyped(''); load(); }
    catch (e) { if (isExpired(e)) onExpire(); else setErr('Could not save. Try again.'); }
    finally { setSaving(false); }
  }
  function onPrice(k: keyof PriceSet, v: string) { setPrices((p) => ({ ...p, [k]: v })); setPriceDirty(true); setPriceSaved(false); setPriceErr(null); }
  function discardPrices() { if (s) { setPrices(s.prices); setPriceDirty(false); setPriceErr(null); } }
  async function savePrices() {
    if (savingPrices || !priceDirty) return;
    setSavingPrices(true); setPriceErr(null);
    try {
      const r = await adminApi.setPricing(prices);
      setS((cur) => (cur ? { ...cur, prices: r.prices } : cur));
      setPrices(r.prices); setPriceDirty(false); setPriceSaved(true);
      setTimeout(() => setPriceSaved(false), 1800);
    } catch (e) { if (isExpired(e)) onExpire(); else setPriceErr('Could not save prices. Try again.'); }
    finally { setSavingPrices(false); }
  }
  async function copyHook() {
    if (!s) return;
    try { await navigator.clipboard.writeText(s.webhook_url); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  }

  if (err && !s) return <div className="adm-loginerr" style={{ maxWidth: 560 }}>{err}</div>;
  if (!s) return <div className="adm-loading"><div className="adm-spin" /></div>;

  const isTest = s.checkout_mode === 'test';
  const target: CheckoutMode = isTest ? 'live' : 'test';
  const liveSub = `real charges · $${s.prices.monthly} / $${s.prices.annual} / $${s.prices.lifetime}`;
  // Friction is asymmetric: going LIVE charges real cards → require a typed
  // confirmation; returning to TEST is reversible and charges nothing → one click.
  const needsType = confirm === 'live';
  const canConfirm = confirm !== null && (!needsType || typed.trim().toUpperCase() === 'GO LIVE');
  const closeModal = () => { if (!saving) { setConfirm(null); setTyped(''); } };

  return (
    <div className="adm-set">
      {/* 01 · Checkout mode */}
      <section id="sec-checkout" className="adm-set-sec">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <Eyebrow>01 · CHECKOUT</Eyebrow>
            <div className="adm-set-title">Checkout mode</div>
          </div>
          <span className="adm-badge" style={{
            color: isTest ? 'var(--amber)' : 'var(--green)',
            background: isTest ? 'rgba(245,181,69,.14)' : 'rgba(52,211,153,.14)',
            display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
          }}>
            <span style={{ width: 7, height: 7, borderRadius: 99, background: isTest ? 'var(--amber)' : 'var(--green)' }} />
            {isTest ? 'TEST MODE' : 'LIVE'}
          </span>
        </div>
        <p className="adm-set-desc" style={{ maxWidth: 520 }}>
          {isTest
            ? 'Upgrades create PayPro test orders — no real money is charged. Use this to verify the full flow end to end.'
            : 'Upgrades go through real PayPro checkout. Customers’ cards are charged for the plan they pick.'}
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13, marginTop: 18 }}>
          <ModeCard active={isTest} accent="var(--amber)" name="Test" sub="no charge · test orders" onPick={() => { setTyped(''); setErr(null); setConfirm('test'); }} />
          <ModeCard active={!isTest} accent="var(--green)" name="Live" sub={liveSub} onPick={() => { setTyped(''); setErr(null); setConfirm('live'); }} />
        </div>

        {target === 'test' && !s.secret_key_set && (
          <SettingBanner tone="warn">Test orders need the checkout secret key (PAYPRO_SECRET_KEY), which isn’t configured — test checkout won’t open until it’s set.</SettingBanner>
        )}
        {target === 'live' && !s.webhook_validation_set && (
          <SettingBanner tone="warn">The IPN validation key (PAYPRO_IPN_VALIDATION_KEY) isn’t configured. Going live without it means purchases can’t be verified and Pro won’t be granted automatically.</SettingBanner>
        )}
        {err && <SettingBanner tone="err">{err}</SettingBanner>}

        <button
          className="adm-cta"
          style={{ marginTop: 16, ...(target === 'live' ? { background: 'var(--green)', boxShadow: '0 8px 22px -10px rgba(52,211,153,.7)' } : {}) }}
          disabled={saving}
          onClick={() => { setTyped(''); setErr(null); setConfirm(target); }}
        >
          {target === 'live' ? 'Switch to Live — charge real money' : 'Switch back to Test mode'}
        </button>

        <div style={{ marginTop: 14, fontFamily: MONO, fontSize: 12.5, color: 'var(--faint)', display: 'flex', flexWrap: 'wrap', gap: '2px 14px' }}>
          <span>source: {s.source === 'db' ? 'admin override' : `config default (${s.config_default})`}</span>
          {s.updated_at && <span>· changed {fmtDateTime(s.updated_at)}{s.updated_by ? ` by ${s.updated_by}` : ''}</span>}
        </div>
      </section>

      {/* 02 · Plans & pricing */}
      <section id="sec-pricing" className="adm-set-sec">
        <Eyebrow>02 · PRICING</Eyebrow>
        <div className="adm-set-title">Plans &amp; pricing</div>
        <p className="adm-set-desc">List prices shown across the app. Editing here updates what customers see — it does <strong style={{ color: 'var(--text)' }}>not</strong> change what PayPro charges. To change the real charge, update the product in PayPro too.</p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 13, marginTop: 16 }} className="adm-price-grid">
          <PriceField label="PRO · MONTHLY" unit="per month" value={prices.monthly} onChange={(v) => onPrice('monthly', v)} />
          <PriceField label="PRO · ANNUAL" unit="per year" value={prices.annual} onChange={(v) => onPrice('annual', v)} />
          <PriceField label="LIFETIME" unit="one-time" value={prices.lifetime} onChange={(v) => onPrice('lifetime', v)} />
        </div>

        {priceErr && <SettingBanner tone="err">{priceErr}</SettingBanner>}

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16 }}>
          {priceSaved && <span style={{ color: 'var(--green)', fontSize: 13, fontWeight: 600 }}>Saved ✓</span>}
          <span style={{ flex: 1 }} />
          <button className="adm-btn" disabled={!priceDirty || savingPrices} onClick={discardPrices}>Discard</button>
          <button className="adm-cta" style={{ width: 'auto', padding: '0 22px' }} disabled={!priceDirty || savingPrices} onClick={savePrices}>{savingPrices ? 'Saving…' : 'Save prices'}</button>
        </div>
      </section>

      {/* 03 · PayPro configuration */}
      <section id="sec-paypro" className="adm-set-sec">
        <Eyebrow>03 · GATEWAY</Eyebrow>
        <div className="adm-set-title" style={{ marginBottom: 14 }}>PayPro configuration</div>
        <KeyStatus name="checkout_secret_key" ok={s.secret_key_set} />
        <KeyStatus name="ipn_validation_key" ok={s.webhook_validation_set} />
        <div style={{ padding: '16px 0 4px' }}>
          <div style={{ fontFamily: MONO, fontSize: 11, fontWeight: 500, letterSpacing: '.07em', color: 'var(--faint)', marginBottom: 8 }}>IPN / WEBHOOK URL</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input readOnly value={s.webhook_url} style={{ fontFamily: MONO, flex: 1, minWidth: 0, fontSize: 14, color: 'var(--text)', background: 'var(--surface-3)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 13px' }} />
            <button className="adm-btn" onClick={copyHook} style={{ whiteSpace: 'nowrap' }}>{copied ? 'Copied ✓' : 'Copy'}</button>
          </div>
        </div>
        <p style={{ margin: '14px 0 0', fontSize: 12.5, color: 'var(--faint)', lineHeight: 1.55 }}>
          These keys live in the server config and are shared across your PayPro account. This page never reads, writes, or rotates them — it only flips the local test / live switch.
        </p>
      </section>

      {/* Confirm modal */}
      {confirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div className="adm-overlay" onClick={closeModal} />
          <div className="adm-card" style={{ position: 'relative', width: 'min(460px, 100%)', borderRadius: 18, padding: '24px 24px 22px', boxShadow: 'var(--shadow)' }}>
            {confirm === 'live' ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 8 }}>
                  <span style={{ width: 36, height: 36, borderRadius: 11, display: 'grid', placeItems: 'center', background: 'rgba(52,211,153,.14)', color: 'var(--green)', flexShrink: 0 }}>
                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
                  </span>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, letterSpacing: '-.01em' }}>Go live — charge real money</h3>
                </div>
                <p style={{ margin: '0 0 14px', color: 'var(--dim)', fontSize: 14, lineHeight: 1.55 }}>
                  From now on, anyone who upgrades is charged for real. Cards will be billed:
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16, fontSize: 13.5 }}>
                  <PriceRow label="Pro · Monthly" v={`$${s.prices.monthly} / mo`} />
                  <PriceRow label="Pro · Annual" v={`$${s.prices.annual} / yr`} />
                  <PriceRow label="Founder Lifetime" v={`$${s.prices.lifetime} once`} />
                </div>
                {!s.webhook_validation_set && <SettingBanner tone="warn">IPN validation key isn’t configured — verified grants won’t work, so paid users may not get Pro.</SettingBanner>}
                <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--dim)', marginTop: 4 }}>Type <strong style={{ color: 'var(--text)', letterSpacing: '.04em' }}>GO LIVE</strong> to confirm</label>
                <input className="adm-input" autoFocus value={typed} placeholder="GO LIVE"
                  onChange={(e) => setTyped(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && canConfirm) apply('live'); }}
                  style={{ marginTop: 6, marginBottom: 16 }} />
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="adm-btn" style={{ flex: 1, justifyContent: 'center' }} disabled={saving} onClick={closeModal}>Cancel</button>
                  <button className="adm-cta" style={{ flex: 1, ...(canConfirm && !saving ? { background: 'var(--green)', boxShadow: '0 8px 22px -10px rgba(52,211,153,.7)' } : {}) }} disabled={!canConfirm || saving} onClick={() => apply('live')}>{saving ? 'Switching…' : 'Go live'}</button>
                </div>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 8 }}>
                  <span style={{ width: 36, height: 36, borderRadius: 11, display: 'grid', placeItems: 'center', background: 'rgba(245,181,69,.14)', color: 'var(--amber)', flexShrink: 0 }}>
                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a4 4 0 0 0-5.4 5.4M9.3 17.7a4 4 0 0 0 5.4-5.4M3 3l18 18" /><path d="M12 3v2M12 19v2M3 12h2M19 12h2" /></svg>
                  </span>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, letterSpacing: '-.01em' }}>Switch to test mode</h3>
                </div>
                <p style={{ margin: '0 0 16px', color: 'var(--dim)', fontSize: 14, lineHeight: 1.55 }}>
                  Upgrades will stop charging real money and become PayPro test orders. Existing customers and their subscriptions are unaffected.
                </p>
                {!s.secret_key_set && <SettingBanner tone="warn">Checkout secret key isn’t configured — test checkout won’t open.</SettingBanner>}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="adm-btn" style={{ flex: 1, justifyContent: 'center' }} disabled={saving} onClick={closeModal}>Cancel</button>
                  <button className="adm-cta" style={{ flex: 1 }} disabled={saving} onClick={() => apply('test')}>{saving ? 'Switching…' : 'Switch to test'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
